import { useCallback, useEffect, useMemo, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCogs as iconSettings } from "@fortawesome/pro-thin-svg-icons";
import { faInfoCircle as iconDebug } from "@fortawesome/pro-thin-svg-icons";
import { faMessageDots as iconChats } from "@fortawesome/pro-thin-svg-icons";
import { faPlus as iconNewChat } from "@fortawesome/pro-thin-svg-icons";
import { faRefresh as iconRefresh } from "@fortawesome/pro-thin-svg-icons";
import { faSignOut as iconDisconnect } from "@fortawesome/pro-thin-svg-icons";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import configEntzy from "components/config/ConfigEntzy";
import { MoreButton } from "components/utils/common/CommonButtons";
import { ActionAlert } from "components/utils/common/CommonLoaders";
import { serviceGraphCall } from "services/graphql/call";

import CnxChatRoom from "./CnxChatRoom";
import {
  createConversationKeyRef,
  decryptConversationEnvelope,
  encryptConversationPlaintext,
  generateSharedConversationKeyBase64,
} from "./CnxCrypto";
import CnxDebug from "./CnxDebug";
import CnxHeader from "./CnxHeader";
import "./CnxIndex.css";

const CNX_SESSION_STORAGE_KEY = "cnx.session";
const LEGACY_CNX_SESSION_STORAGE_KEY = "cnx.experimental.session";
const CNX_CONVERSATION_KEYS_STORAGE_KEY = "cnx.conversation_keys";

function buildDefaultCnxBaseUrl() {
  const environment =
    configEntzy.APP_ENV === "local" ? "develop" : configEntzy.APP_ENV;

  if (environment === "production") {
    return "https://api.production.cnx.to";
  }

  return `https://api.${environment}.cnx.to`;
}

const DEFAULT_CNX_BASE_URL = buildDefaultCnxBaseUrl();

function normalizeSessionBaseUrl(value) {
  const trimmedValue = String(value || "").trim();

  if (!trimmedValue) {
    return DEFAULT_CNX_BASE_URL;
  }

  if (trimmedValue === "https://api.develop.cnx.to") {
    return "https://api.develop.cnx.to";
  }

  if (trimmedValue === "https://api.production.cnx.to") {
    return "https://api.production.cnx.to";
  }

  if (trimmedValue === "https://api.develop.cnx.to/api") {
    return "https://api.develop.cnx.to";
  }

  if (trimmedValue === "https://api.production.cnx.to/api") {
    return "https://api.production.cnx.to";
  }

  if (trimmedValue === "http://127.0.0.1:8080") {
    return DEFAULT_CNX_BASE_URL;
  }

  if (
    trimmedValue === "http://127.0.0.1:8080/api" ||
    trimmedValue === "http://localhost:8080" ||
    trimmedValue === "http://localhost:8080/api"
  ) {
    return DEFAULT_CNX_BASE_URL;
  }

  return trimmedValue;
}

function buildBootstrapDraft(user) {
  return {
    providerUserId: user && user.identity ? user.identity : "",
    email: user && user.email ? user.email : "",
    name: user && user.name ? user.name : "",
  };
}

function buildCnxSiteUrl(baseUrl) {
  const normalizedBaseUrl = normalizeSessionBaseUrl(baseUrl);

  try {
    const parsed = new URL(normalizedBaseUrl);
    const host = parsed.host.replace(/^api\./, "");

    return `https://${host}`;
  } catch (error) {
    return "https://develop.cnx.to";
  }
}

function waitForPasskeyPopupResult(popupWindow, popupOrigin, challengeId) {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("Passkey verification timed out."));
    }, 120000);

    const closedInterval = window.setInterval(() => {
      if (!popupWindow || popupWindow.closed) {
        cleanup();
        reject(
          new Error(
            "Passkey verification window was closed before completion.",
          ),
        );
      }
    }, 500);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(closedInterval);
      window.removeEventListener("message", handleMessage);
    };

    const handleMessage = (event) => {
      if (event.origin !== popupOrigin) {
        return;
      }

      if (event.data?.type === "cnx:passkey:error") {
        cleanup();
        reject(new Error(event.data.message || "Passkey verification failed."));
        return;
      }

      if (event.data?.type !== "cnx:passkey:result") {
        return;
      }

      if (
        challengeId &&
        event.data.challengeId &&
        event.data.challengeId !== challengeId
      ) {
        return;
      }

      cleanup();
      resolve(String(event.data.credential || ""));
    };

    window.addEventListener("message", handleMessage);
  });
}

function waitForPasskeyPopupReady(popupWindow, popupOrigin) {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      reject(new Error("Passkey window did not become ready in time."));
    }, 15000);

    const closedInterval = window.setInterval(() => {
      if (!popupWindow || popupWindow.closed) {
        cleanup();
        reject(
          new Error(
            "Passkey verification window was closed before it became ready.",
          ),
        );
      }
    }, 500);

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      window.clearInterval(closedInterval);
      window.removeEventListener("message", handleMessage);
    };

    const handleMessage = (event) => {
      if (event.origin !== popupOrigin) {
        return;
      }

      if (event.data?.type !== "cnx:passkey:ready") {
        return;
      }

      cleanup();
      resolve();
    };

    window.addEventListener("message", handleMessage);
  });
}

function openPasskeyPopup(cnxSiteUrl) {
  const popupUrl = `${cnxSiteUrl.replace(/\/$/, "")}/passkey/popup?provider_origin=${encodeURIComponent(window.location.origin)}`;
  const width = 480;
  const height = 720;
  const dualScreenLeft =
    typeof window.screenLeft === "number" ? window.screenLeft : window.screenX;
  const dualScreenTop =
    typeof window.screenTop === "number" ? window.screenTop : window.screenY;
  const viewportWidth = window.outerWidth || window.innerWidth || width;
  const viewportHeight = window.outerHeight || window.innerHeight || height;
  const left = Math.max(
    0,
    Math.round(dualScreenLeft + (viewportWidth - width) / 2),
  );
  const top = Math.max(
    0,
    Math.round(dualScreenTop + (viewportHeight - height) / 2),
  );

  return window.open(
    popupUrl,
    "cnx-passkey-popup",
    `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
  );
}

function notifyPasskeyPopupCompletion(popupWindow, cnxSiteUrl, message) {
  if (!popupWindow || popupWindow.closed) {
    return;
  }

  try {
    popupWindow.postMessage(
      {
        type: "cnx:passkey:complete",
        message,
      },
      new URL(cnxSiteUrl).origin,
    );
  } catch (error) {
    // Best-effort only. The provider flow can continue even if the popup
    // doesn't receive the completion message.
  }
}

function extractGraphResponseErrorText(response) {
  return [
    response?.message,
    response?.data?.message,
    response?.data?.errors ? JSON.stringify(response.data.errors) : null,
  ]
    .filter(Boolean)
    .join(" ");
}

function extractGraphResponseErrors(response) {
  const responseErrors = Array.isArray(response?.data?.errors)
    ? response.data.errors
    : [];

  return responseErrors
    .filter((error) => error && typeof error === "object")
    .map((error) => ({
      message: String(error.message || ""),
      errorType: String(
        error.errorType ||
          error.error_type ||
          error.extensions?.errorType ||
          error.extensions?.code ||
          "",
      ),
    }));
}

function classifyIntegrationContractError(response) {
  const structuredErrors = extractGraphResponseErrors(response);

  if (
    structuredErrors.some(
      (error) =>
        error.errorType === "GraphQLFieldValidationError" ||
        error.message.includes("Validation error of type FieldUndefined") ||
        error.message.includes("Validation error of type UnknownArgument") ||
        error.message.includes("Cannot query field") ||
        error.message.includes("Unknown field argument"),
    )
  ) {
    return true;
  }

  const normalized = extractGraphResponseErrorText(response).toLowerCase();

  return (
    normalized.includes("graphqlfieldvalidationerror") ||
    normalized.includes("validation error of type fieldundefined") ||
    normalized.includes("validation error of type unknownargument") ||
    normalized.includes("unknown field argument") ||
    normalized.includes("fieldundefined") ||
    normalized.includes(
      "field 'chat' in type 'cnxmanagerresult' is undefined",
    ) ||
    normalized.includes("clientmessageid") ||
    normalized.includes("ciphertextenvelope") ||
    normalized.includes("cannot query field") ||
    normalized.includes("provider backend did not return a valid")
  );
}

function buildIntegrationErrorResult(errorText) {
  return {
    status: "integration_error",
    message:
      "Entzy chat is temporarily out of sync with the provider CNX API contract. Retry after the latest backend and AppSync updates finish deploying.",
    reason: errorText || undefined,
  };
}

function buildBootstrapUnavailableResult() {
  return {
    status: "error",
    message:
      "Entzy chat could not get a valid CNX bootstrap response yet. Retry activation once the provider backend is ready.",
  };
}

function resolveActivationStatus(status, fallback = "error") {
  const normalized = String(status || "").trim();
  return normalized || fallback;
}

function normalizeBootstrapPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  if (payload.result && typeof payload.result === "object") {
    const normalizedManagedResult = normalizeBootstrapPayload(payload.result);

    if (!normalizedManagedResult) {
      return null;
    }

    const browserSession =
      payload.browserSession && typeof payload.browserSession === "object"
        ? payload.browserSession
        : null;

    const mergedSession = normalizedManagedResult.session || {};

    if (browserSession) {
      if (browserSession.accessToken) {
        mergedSession.access_token = browserSession.accessToken;
      }
      if (browserSession.expiresAtEpochSeconds) {
        mergedSession.expires_at_epoch_seconds =
          browserSession.expiresAtEpochSeconds;
      }
      if (browserSession.cnxUserId) {
        mergedSession.cnx_user_id = browserSession.cnxUserId;
      }
      if (browserSession.cnxProviderId) {
        mergedSession.cnx_provider_id = browserSession.cnxProviderId;
      }
    }

    return {
      ...normalizedManagedResult,
      ...(Object.keys(mergedSession).length > 0
        ? { session: mergedSession }
        : {}),
      ...(payload.providerSessionId
        ? { providerSessionId: payload.providerSessionId }
        : {}),
      ...(payload.set_cookie ? { set_cookie: payload.set_cookie } : {}),
      ...(payload.clear_cookie ? { clear_cookie: payload.clear_cookie } : {}),
      ...(payload.cookie ? { cookie: payload.cookie } : {}),
    };
  }

  if (payload.access_token && payload.cnx_user_id) {
    return {
      access_token: payload.access_token,
      cnx_user_id: payload.cnx_user_id,
      cnx_provider_id: payload.cnx_provider_id,
    };
  }

  if (payload.status) {
    return {
      status: payload.status || "session_ready",
      next_action: payload.nextAction,
      message: payload.message,
      challenge: payload.challenge
        ? {
            challenge_id: payload.challenge.challengeId,
            challenge_type: payload.challenge.challengeType,
            methods: payload.challenge.methods,
            public_key: payload.challenge.publicKey,
          }
        : undefined,
      session: payload.session
        ? {
            access_token: payload.session.accessToken,
            refresh_token: payload.session.refreshToken,
            expires_at_epoch_seconds: payload.session.expiresAtEpochSeconds,
            cnx_user_id: payload.session.cnxUserId,
            cnx_provider_id: payload.session.cnxProviderId,
          }
        : undefined,
      providerSessionId: payload.providerSessionId,
      set_cookie: payload.set_cookie,
      clear_cookie: payload.clear_cookie,
      cookie: payload.cookie,
      chat: payload.chat
        ? {
            operation: payload.chat.operation,
            status: payload.chat.status,
            nextCursor: payload.chat.nextCursor,
            conversations: payload.chat.conversations,
            messages: payload.chat.messages,
          }
        : undefined,
      prefill: payload.prefill
        ? {
            provider_user: payload.prefill.providerUser
              ? {
                  id: payload.prefill.providerUser.id,
                  email: payload.prefill.providerUser.email,
                  name: payload.prefill.providerUser.name,
                }
              : undefined,
            suggested_cnx_pointer: payload.prefill.suggestedCnxPointer,
          }
        : undefined,
    };
  }

  return payload;
}

function buildInitialSteps(user) {
  return [
    {
      id: "provider",
      title: "Provider Setup",
      status: "ready",
      detail:
        "Entzy should already exist as a CNX provider before this proof starts.",
    },
    {
      id: "session",
      title: "CNX Session",
      status: user && user.connected ? "pending" : "blocked",
      detail:
        user && user.connected
          ? "Use the logged-in Entzy identity to create a provider-backed CNX session, then restore it later through the provider session id carrier."
          : "Connect to Entzy first so the CNX proof can reuse your known profile context.",
    },
    {
      id: "conversation",
      title: "Direct Conversation",
      status: "pending",
      detail:
        "Load conversation summaries through the provider backend first, then create or reuse a direct CNX conversation when needed.",
    },
    {
      id: "message",
      title: "Encrypted Message",
      status: "pending",
      detail:
        "Keep encryption and envelope preparation in the browser, while the longer-term CNX transport shifts behind the provider backend.",
    },
    {
      id: "read",
      title: "Sync And Read",
      status: "pending",
      detail:
        "Sync conversation state through the provider-backed CNX transport and confirm read-state updates cleanly.",
    },
  ];
}

function getActivationMeta(outcome) {
  switch (outcome) {
    case "session_ready":
      return {
        title: "Chat is active",
        detail:
          "CNX session ready. Entzy can now continue into the DM conversation bootstrap.",
      };
    case "registration_required":
      return {
        title: "CNX setup needed",
        detail:
          "This user still needs a CNX account. The activation flow should guide them through setup and then continue into provider linking automatically.",
      };
    case "link_required":
      return {
        title: "Linking your chat provider",
        detail:
          "This user likely already has a CNX account but is not linked to Entzy yet. The next step is to create or confirm that provider link, then retry bootstrap.",
      };
    case "verification_required":
      return {
        title: "Verification required",
        detail:
          "CNX needs an additional confirmation step before chat can activate for this provider user.",
      };
    case "mfa_required":
      return {
        title: "Multi-factor authentication required",
        detail:
          "CNX has verified the linked user and now needs a second factor before chat can activate.",
      };
    case "integration_error":
      return {
        title: "Provider integration update needed",
        detail:
          "Entzy received an invalid CNX API response shape. This is a provider integration issue rather than a user registration problem.",
      };
    default:
      return {
        title: "Activate chat",
        detail:
          "Entzy Chat is powered by CNX. Activation should register or link the user if needed, then continue directly into the right conversation.",
      };
  }
}

function buildActivationSupportDraft(user) {
  const name = user && user.name ? user.name : "";
  return {
    cnxPointer: name
      ? `::${name.replace(/^@/, "").replace(/\s+/g, ".").toLowerCase()}`
      : "",
    challengeId: "",
    challengeType: "",
    verificationCode: "",
    methods: [],
    publicKey: "",
    credential: "",
    verificationSource: "createSession",
    agreedToLink: true,
  };
}

function persistSessionDraft(nextSession) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(LEGACY_CNX_SESSION_STORAGE_KEY);
  window.localStorage.setItem(
    CNX_SESSION_STORAGE_KEY,
    JSON.stringify({
      baseUrl: normalizeSessionBaseUrl(nextSession.baseUrl),
      providerSessionId: nextSession.providerSessionId || "",
      expiresAtEpochSeconds: nextSession.expiresAtEpochSeconds || "",
      accountId: nextSession.accountId || "",
    }),
  );
}

function clearPersistedSessionDraft() {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(CNX_SESSION_STORAGE_KEY);
  window.localStorage.removeItem(LEGACY_CNX_SESSION_STORAGE_KEY);
}

function buildSessionDraft() {
  if (typeof window === "undefined") {
    return {
      baseUrl: DEFAULT_CNX_BASE_URL,
      providerSessionId: "",
      expiresAtEpochSeconds: "",
      accountId: "",
    };
  }

  const persisted =
    window.localStorage.getItem(CNX_SESSION_STORAGE_KEY) ||
    window.localStorage.getItem(LEGACY_CNX_SESSION_STORAGE_KEY);

  if (!persisted) {
    return {
      baseUrl: DEFAULT_CNX_BASE_URL,
      providerSessionId: "",
      expiresAtEpochSeconds: "",
      accountId: "",
    };
  }

  try {
    const parsed = JSON.parse(persisted);
    return {
      baseUrl: normalizeSessionBaseUrl(parsed.baseUrl),
      providerSessionId: parsed.providerSessionId || "",
      expiresAtEpochSeconds: parsed.expiresAtEpochSeconds || "",
      accountId: parsed.accountId || "",
    };
  } catch (error) {
    return {
      baseUrl: DEFAULT_CNX_BASE_URL,
      providerSessionId: "",
      expiresAtEpochSeconds: "",
      accountId: "",
    };
  }
}

function buildConversationKeysDraft() {
  if (typeof window === "undefined") {
    return {};
  }

  const persisted = window.localStorage.getItem(
    CNX_CONVERSATION_KEYS_STORAGE_KEY,
  );

  if (!persisted) {
    return {};
  }

  try {
    const parsed = JSON.parse(persisted);

    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    return Object.entries(parsed).reduce(
      (draft, [conversationId, keyValue]) => {
        const trimmedConversationId = String(conversationId || "").trim();
        const trimmedKeyValue = String(keyValue || "").trim();

        if (!trimmedConversationId || !trimmedKeyValue) {
          return draft;
        }

        draft[trimmedConversationId] = trimmedKeyValue;
        return draft;
      },
      {},
    );
  } catch (error) {
    return {};
  }
}

function persistConversationKeys(nextConversationKeys) {
  if (typeof window === "undefined") {
    return;
  }

  const normalizedDraft = Object.entries(nextConversationKeys || {}).reduce(
    (draft, [conversationId, keyValue]) => {
      const trimmedConversationId = String(conversationId || "").trim();
      const trimmedKeyValue = String(keyValue || "").trim();

      if (!trimmedConversationId || !trimmedKeyValue) {
        return draft;
      }

      draft[trimmedConversationId] = trimmedKeyValue;
      return draft;
    },
    {},
  );

  window.localStorage.setItem(
    CNX_CONVERSATION_KEYS_STORAGE_KEY,
    JSON.stringify(normalizedDraft),
  );
}

function parseProviderSessionIdFromCookieString(value) {
  if (!value) {
    return "";
  }

  const match = String(value).match(/(?:^|;\s*)cnx_session=([^;]+)/i);
  return match ? match[1] : "";
}

function readProviderSessionIdFromPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return "";
  }

  return (
    payload.providerSessionId ||
    payload.provider_session_id ||
    (payload.session && payload.session.providerSessionId) ||
    (payload.session && payload.session.provider_session_id) ||
    parseProviderSessionIdFromCookieString(payload.set_cookie) ||
    parseProviderSessionIdFromCookieString(payload.clear_cookie) ||
    parseProviderSessionIdFromCookieString(
      payload.cookie && (payload.cookie.set || payload.cookie.clear),
    ) ||
    ""
  );
}

function shouldClearPersistedSessionOnRestoreFailure(message) {
  const normalized = String(message || "").toLowerCase();

  return (
    normalized.includes("no stored cnx session") ||
    normalized.includes("session restore failed") ||
    normalized.includes("provider user mismatch") ||
    normalized.includes("missing refresh token") ||
    normalized.includes("refresh not ready") ||
    normalized.includes("missing access token")
  );
}

function createMessageItem({
  id,
  senderRole,
  senderLabel,
  senderPointer,
  body,
  deliveryState,
  timestamp,
}) {
  return {
    id,
    senderRole,
    senderLabel,
    senderPointer,
    body,
    deliveryState,
    timestamp,
  };
}

function buildInitialConversations() {
  return [];
}

function resetBootstrapDebugState() {
  return {
    lastAction: "createSession",
    rawResponse: null,
    normalizedResponse: null,
    lastError: "",
    conversationLoad: {
      status: "idle",
      rawResponse: null,
      normalizedResponse: null,
      lastError: "",
    },
  };
}

function formatTimestamp(value) {
  if (!value) {
    return "Now";
  }

  return new Date(value).toLocaleString();
}

function parseEpochMillis(value, fallback = Date.now()) {
  const parsedValue =
    typeof value === "number"
      ? value
      : Number.parseInt(String(value || ""), 10);

  return Number.isFinite(parsedValue) ? parsedValue : fallback;
}

function buildDefaultRoomTitle(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);

  return `Chat ${date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
  })}`;
}

function mapConversationSummary(summary) {
  const conversation = summary.conversation;
  const title =
    summary.displayTitle ||
    summary.display_title ||
    (conversation.settings && conversation.settings.title
      ? conversation.settings.title
      : buildDefaultRoomTitle());
  const subtitle =
    summary.displaySubtitle ||
    summary.display_subtitle ||
    (conversation.kind === "group"
      ? "Group conversation"
      : "No members added yet");
  const sourceProviderKey =
    summary.sourceProviderKey ||
    summary.source_provider_key ||
    conversation.sourceProviderKey ||
    conversation.source_provider_key ||
    "";
  const rawLastActivityAtEpochMs =
    summary.lastActivityAtEpochMs ||
    summary.last_activity_at_epoch_ms ||
    conversation.updatedAtEpochMs ||
    conversation.updated_at_epoch_ms ||
    Date.now();
  const lastActivityAtEpochMs = parseEpochMillis(rawLastActivityAtEpochMs);

  return {
    id: conversation.id,
    title,
    providerLabel: sourceProviderKey || "CNX direct",
    providerUserId: "Provider mapping pending",
    recipientPointer: subtitle,
    unreadCount: summary.unread_count ?? summary.unreadCount ?? 0,
    lastReadSequence:
      summary.last_read_sequence ?? summary.lastReadSequence ?? 0,
    participantCount:
      summary.participant_count ?? summary.participantCount ?? 0,
    lastActivityAtEpochMs,
    kind: conversation.kind || "direct",
    status: conversation.status || "active",
    settings: conversation.settings || {},
    source: "cnx",
    messages: summary.last_message || summary.lastMessage ? [] : [],
    messagesLoaded: false,
    participants: [],
    participantsLoaded: false,
  };
}

function mergeConversationSummaryWithExisting(
  mappedConversation,
  existingConversation,
) {
  if (!existingConversation) {
    return mappedConversation;
  }

  return {
    ...mappedConversation,
    messages:
      existingConversation.source === "cnx" &&
      Array.isArray(existingConversation.messages) &&
      existingConversation.messages.length
        ? existingConversation.messages
        : mappedConversation.messages,
    messagesLoaded:
      existingConversation.source === "cnx" &&
      typeof existingConversation.messagesLoaded === "boolean"
        ? existingConversation.messagesLoaded
        : mappedConversation.messagesLoaded,
    participants:
      Array.isArray(existingConversation.participants) &&
      existingConversation.participants.length
        ? existingConversation.participants
        : mappedConversation.participants,
    participantsLoaded:
      typeof existingConversation.participantsLoaded === "boolean"
        ? existingConversation.participantsLoaded
        : mappedConversation.participantsLoaded,
  };
}

function mapConversationParticipant(participant) {
  if (!participant || typeof participant !== "object") {
    return null;
  }

  const label =
    participant.pointer ||
    participant.name ||
    participant.displayName ||
    participant.display_name ||
    participant.account?.name ||
    participant.account?.displayName ||
    participant.account?.display_name ||
    participant.email ||
    participant.account?.email ||
    participant.id ||
    participant.account?.id ||
    "Unknown participant";
  const accountId =
    participant.id || participant.account?.id || participant.pointer || "";

  if (!accountId) {
    return null;
  }

  return {
    id: accountId,
    label,
    pointer: participant.pointer || participant.account?.pointer || "",
    name:
      participant.name ||
      participant.account?.name ||
      participant.account?.displayName ||
      participant.account?.display_name ||
      "",
    role: participant.role || "",
  };
}

function normalizeCiphertextEnvelope(value) {
  if (value && typeof value === "object") {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      return {};
    }
  }

  return {};
}

async function mapChatMessageResultToMessage(
  message,
  accountId,
  conversationKeyBase64,
) {
  const senderRole =
    accountId && message.senderAccountId === accountId ? "me" : "them";
  const ciphertextEnvelope = normalizeCiphertextEnvelope(
    message.ciphertextEnvelope,
  );
  let body = `Encrypted payload (${ciphertextEnvelope.content_kind || "unknown"})`;

  if (conversationKeyBase64) {
    try {
      body = await decryptConversationEnvelope({
        ciphertextEnvelope,
        conversationKeyBase64,
      });
    } catch (error) {
      body = `Unable to decrypt encrypted payload (${ciphertextEnvelope.content_kind || "unknown"})`;
    }
  }

  return createMessageItem({
    id: message.id || `msg-${message.sequence || Date.now()}`,
    senderRole,
    senderLabel:
      senderRole === "me"
        ? "Me"
        : `Account ${String(message.senderAccountId || "unknown").slice(-8)}`,
    senderPointer: "provider-backend",
    body,
    deliveryState: message.status || "received",
    timestamp: formatTimestamp(
      message.clientCreatedAt || ciphertextEnvelope.client_created_at,
    ),
  });
}

function normalizeListConversationsResponse(response) {
  if (!response?.success || !response.data) {
    return null;
  }

  const payload = response.data;
  const chatPayload =
    payload.chat && typeof payload.chat === "object" ? payload.chat : payload;
  const conversations = Array.isArray(chatPayload.conversations)
    ? chatPayload.conversations
    : null;

  if (!conversations) {
    return null;
  }

  return {
    operation: chatPayload.operation || "listConversations",
    status: chatPayload.status || payload.status || "ok",
    conversations,
    nextCursor:
      chatPayload.nextCursor ||
      chatPayload.next_cursor ||
      payload.nextCursor ||
      payload.next_cursor ||
      null,
  };
}

function normalizeSyncThreadResponse(response) {
  if (!response?.success || !response.data) {
    return null;
  }

  const payload = response.data;
  const chatPayload =
    payload.chat && typeof payload.chat === "object" ? payload.chat : null;
  const messages = Array.isArray(chatPayload?.messages)
    ? chatPayload.messages
    : null;

  if (!chatPayload || !messages) {
    return null;
  }

  return {
    operation: chatPayload.operation || "syncThread",
    status: chatPayload.status || payload.status || "ok",
    messages,
  };
}

function normalizeCreateConversationResponse(response) {
  if (!response?.success || !response.data) {
    return null;
  }

  const payload = response.data;
  const chatPayload =
    payload.chat && typeof payload.chat === "object" ? payload.chat : null;
  const conversations = Array.isArray(chatPayload?.conversations)
    ? chatPayload.conversations
    : null;

  if (!chatPayload || !conversations || !conversations.length) {
    return null;
  }

  return {
    operation: chatPayload.operation || "createConversation",
    status: chatPayload.status || payload.status || "ok",
    conversations,
    messages: Array.isArray(chatPayload.messages) ? chatPayload.messages : [],
  };
}

function normalizeAddParticipantsResponse(response) {
  if (!response?.success || !response.data) {
    return null;
  }

  const payload = response.data;
  const chatPayload =
    payload.chat && typeof payload.chat === "object" ? payload.chat : payload;
  const conversations = Array.isArray(chatPayload?.conversations)
    ? chatPayload.conversations
    : null;

  if (
    !chatPayload ||
    (chatPayload.status === "ok" && (!conversations || !conversations.length))
  ) {
    return null;
  }

  return {
    operation: chatPayload.operation || "addParticipants",
    status: chatPayload.status || payload.status || "ok",
    conversations: conversations || [],
    lookup:
      chatPayload.lookup && typeof chatPayload.lookup === "object"
        ? chatPayload.lookup
        : null,
    message: payload.message || chatPayload.message || "",
  };
}

function normalizeRemoveParticipantsResponse(response) {
  if (!response?.success || !response.data) {
    return null;
  }

  const payload = response.data;
  const chatPayload =
    payload.chat && typeof payload.chat === "object" ? payload.chat : payload;
  const conversations = Array.isArray(chatPayload?.conversations)
    ? chatPayload.conversations
    : null;

  if (
    !chatPayload ||
    (chatPayload.status === "ok" && (!conversations || !conversations.length))
  ) {
    return null;
  }

  return {
    operation: chatPayload.operation || "removeParticipants",
    status: chatPayload.status || payload.status || "ok",
    conversations: conversations || [],
    message: payload.message || chatPayload.message || "",
  };
}

function normalizeListParticipantsResponse(response) {
  if (!response?.success || !response.data) {
    return null;
  }

  const payload = response.data;
  const chatPayload =
    payload.chat && typeof payload.chat === "object" ? payload.chat : payload;
  const participants = Array.isArray(chatPayload?.participants)
    ? chatPayload.participants
    : null;

  if (!chatPayload || !participants) {
    return null;
  }

  return {
    operation: chatPayload.operation || "listParticipants",
    status: chatPayload.status || payload.status || "ok",
    participants: participants || [],
  };
}

function normalizeUpdateConversationResponse(response) {
  if (!response?.success || !response.data) {
    return null;
  }

  const payload = response.data;
  const chatPayload =
    payload.chat && typeof payload.chat === "object" ? payload.chat : payload;
  const conversations = Array.isArray(chatPayload?.conversations)
    ? chatPayload.conversations
    : null;

  if (!chatPayload || !conversations || !conversations.length) {
    return null;
  }

  return {
    operation: chatPayload.operation || "updateConversation",
    status: chatPayload.status || payload.status || "ok",
    conversations,
    message: payload.message || chatPayload.message || "",
  };
}

function normalizeSendMessageResponse(response) {
  if (!response?.success || !response.data) {
    return null;
  }

  const payload = response.data;
  const chatPayload =
    payload.chat && typeof payload.chat === "object" ? payload.chat : payload;
  const messages = Array.isArray(chatPayload?.messages)
    ? chatPayload.messages
    : null;

  if (!chatPayload || !chatPayload.status) {
    return null;
  }

  return {
    operation: chatPayload.operation || "sendMessage",
    status: chatPayload.status,
    messages: messages || [],
    message: payload.message || chatPayload.message || "",
  };
}

function normalizeConversationCryptoResponse(response) {
  if (!response?.success || !response.data) {
    return null;
  }

  const payload = response.data;
  const chatPayload =
    payload.chat && typeof payload.chat === "object" ? payload.chat : payload;
  const cryptoPayload =
    chatPayload?.crypto && typeof chatPayload.crypto === "object"
      ? chatPayload.crypto
      : chatPayload;
  const manifest =
    cryptoPayload?.manifest && typeof cryptoPayload.manifest === "object"
      ? cryptoPayload.manifest
      : null;
  const keyBox =
    cryptoPayload?.keyBox && typeof cryptoPayload.keyBox === "object"
      ? cryptoPayload.keyBox
      : cryptoPayload?.key_box && typeof cryptoPayload.key_box === "object"
        ? cryptoPayload.key_box
        : null;

  if (!manifest && !keyBox) {
    return null;
  }

  return {
    operation: chatPayload?.operation || "conversationCrypto",
    status: chatPayload?.status || payload.status || "ok",
    manifest,
    keyBox,
  };
}

function buildProofWrappedConversationKeyEnvelope(conversationKeyBase64) {
  return {
    version: "cnx.room-key-box.v1",
    encoding: "base64",
    wrapMode: "proof-direct-local-key",
    ciphertext: String(conversationKeyBase64 || "").trim(),
  };
}

function readConversationKeyFromWrappedEnvelope(value) {
  if (!value || typeof value !== "object") {
    return "";
  }

  if (typeof value.ciphertext === "string") {
    return value.ciphertext.trim();
  }

  if (typeof value.wrappedKey === "string") {
    return value.wrappedKey.trim();
  }

  return "";
}

function mapBootstrapGraphResponse(response, bootstrapDraft) {
  if (response.success && response.data) {
    return normalizeBootstrapPayload(response.data);
  }

  const errorText = extractGraphResponseErrorText(response);

  if (classifyIntegrationContractError(response)) {
    return buildIntegrationErrorResult(errorText);
  }

  if (/provider connection not found/i.test(errorText)) {
    return {
      status: "link_required",
      message:
        "CNX recognized the provider but did not find an active Entzy link for this user yet. The next step is to create or confirm the provider link, then retry activation.",
      prefill: {
        provider_user: {
          id: bootstrapDraft.providerUserId || undefined,
          email: bootstrapDraft.email || undefined,
          name: bootstrapDraft.name || undefined,
        },
      },
    };
  }

  return buildBootstrapUnavailableResult();
}

function normalizeActivationActionResponse(response, fallbackResult) {
  if (response.success && response.data) {
    const normalized = normalizeBootstrapPayload(response.data);
    return normalized || fallbackResult;
  }

  const errorText = extractGraphResponseErrorText(response);
  if (classifyIntegrationContractError(response)) {
    return buildIntegrationErrorResult(errorText);
  }

  return fallbackResult;
}

function isRecoverableVerificationError(errorMessage) {
  const normalized = (errorMessage || "").toLowerCase();
  return (
    normalized.includes("invalid verification code") ||
    normalized.includes("verification code has expired") ||
    normalized.includes("auth challenge expired") ||
    normalized.includes("auth challenge not found")
  );
}

function isChallengeRestartRequired(message) {
  const normalized = (message || "").toLowerCase();
  return (
    normalized.includes("auth challenge not found") ||
    normalized.includes("auth challenge expired") ||
    normalized.includes("verification code has expired")
  );
}

function CnxIndexExperimental(props) {
  const user = props.user;
  const [bootstrapDraft, setBootstrapDraft] = useState(() =>
    buildBootstrapDraft(user),
  );
  const [bootstrapOutcome, setBootstrapOutcome] = useState(
    "Awaiting provider bootstrap",
  );
  const [recipientPointer, setRecipientPointer] = useState("");
  const [memberPointer, setMemberPointer] = useState("");
  const [conversationTitleDraft, setConversationTitleDraft] = useState("");
  const [isEditingConversationTitle, setIsEditingConversationTitle] =
    useState(false);
  const [chatNotice, setChatNotice] = useState(null);
  const [messageBody, setMessageBody] = useState("");
  const [ciphertext, setCiphertext] = useState("");
  const [nonce, setNonce] = useState("");
  const [conversationKeysById, setConversationKeysById] = useState(() =>
    buildConversationKeysDraft(),
  );
  const [conversationCryptoStatusById, setConversationCryptoStatusById] =
    useState({});
  const [steps, setSteps] = useState(() => buildInitialSteps(user));
  const [activity, setActivity] = useState([]);
  const [sessionForm, setSessionForm] = useState(() => buildSessionDraft());
  const [sessionReady, setSessionReady] = useState(false);
  const [loadingAction, setLoadingAction] = useState("");
  const [activeChatView, setActiveChatView] = useState("conversations");
  const [conversations, setConversations] = useState(() =>
    buildInitialConversations(),
  );
  const [selectedConversationId, setSelectedConversationId] = useState("");
  const [activationSupport, setActivationSupport] = useState(() =>
    buildActivationSupportDraft(user),
  );
  const [debugState, setDebugState] = useState(() =>
    resetBootstrapDebugState(),
  );
  const [debugDrawerOpen, setDebugDrawerOpen] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [activeMfaMethod, setActiveMfaMethod] = useState("");
  const [passkeyOverlayOpen, setPasskeyOverlayOpen] = useState(false);
  const [passkeyOverlayMessage, setPasskeyOverlayMessage] = useState(
    "Open the secure passkey window to continue.",
  );
  const activationMeta = useMemo(
    () => getActivationMeta(sessionReady ? "session_ready" : bootstrapOutcome),
    [bootstrapOutcome, sessionReady],
  );
  const cnxSiteUrl = useMemo(
    () => buildCnxSiteUrl(sessionForm.baseUrl || DEFAULT_CNX_BASE_URL),
    [sessionForm.baseUrl],
  );
  const verificationAction =
    activationSupport.verificationSource ||
    (debugState.lastAction === "createSession" ||
    debugState.lastAction === "confirmProviderLink"
      ? "createSession"
      : "registerUser");
  const verificationIsLogin = verificationAction === "createSession";
  const mfaMethods = Array.isArray(activationSupport.methods)
    ? activationSupport.methods
    : [];
  const mfaSupportsTotp =
    mfaMethods.includes("totp") || mfaMethods.includes("authenticator_app");
  const mfaSupportsPasskey = mfaMethods.includes("passkey");
  const hasMultipleMfaMethods =
    [mfaSupportsTotp, mfaSupportsPasskey].filter(Boolean).length > 1;
  const isActionLoading = (action) => loadingAction === action;
  const isBusy = Boolean(loadingAction);
  const isRestoringSession = isActionLoading("restoreSession");
  const navIconSx = {
    border: "1px solid rgba(255,167,96,0.18)",
    color: "rgba(255,236,220,0.9)",
    background:
      "linear-gradient(180deg, rgba(17,20,28,0.96), rgba(10,12,18,0.96))",
    borderRadius: 999,
    width: 44,
    height: 44,
    boxShadow:
      "0 0 0 1px rgba(255,255,255,0.03) inset, 0 0 12px rgba(255,133,59,0.06)",
    "&:hover": {
      background:
        "linear-gradient(180deg, rgba(25,29,39,0.98), rgba(13,16,24,0.98))",
      borderColor: "rgba(255,177,118,0.34)",
    },
  };
  const getNavIconSx = (view) =>
    view === activeChatView
      ? {
          ...navIconSx,
          borderColor: "rgba(255,177,118,0.42)",
          color: "#ffd7b6",
          boxShadow:
            "0 0 0 1px rgba(255,255,255,0.04) inset, 0 0 18px rgba(255,133,59,0.14)",
        }
      : navIconSx;

  useEffect(() => {
    if (mfaSupportsPasskey && !mfaSupportsTotp) {
      setActiveMfaMethod("passkey");
      return;
    }

    if (mfaSupportsTotp && !mfaSupportsPasskey) {
      setActiveMfaMethod("totp");
      return;
    }

    if (mfaSupportsTotp && mfaSupportsPasskey) {
      setActiveMfaMethod((current) =>
        current === "" || current === "totp" || current === "passkey"
          ? current
          : "",
      );
      return;
    }

    setActiveMfaMethod("");
  }, [mfaSupportsPasskey, mfaSupportsTotp]);

  useEffect(() => {
    if (bootstrapOutcome !== "mfa_required") {
      setActiveMfaMethod("");
    }
  }, [bootstrapOutcome]);

  useEffect(() => {
    const popupOrigin = new URL(cnxSiteUrl).origin;

    const handlePasskeyPopupMessage = (event) => {
      if (event.origin !== popupOrigin) {
        return;
      }

      if (event.data?.type === "cnx:passkey:popup-closed") {
        setPasskeyOverlayOpen(false);
        setPasskeyOverlayMessage("The secure passkey window was closed.");
      }
    };

    window.addEventListener("message", handlePasskeyPopupMessage);
    return () =>
      window.removeEventListener("message", handlePasskeyPopupMessage);
  }, [cnxSiteUrl]);
  const navIconInnerSx = {
    fontSize: "0.95rem",
  };
  const buildCnxManagerVariables = useCallback(
    (variables = {}, providerSessionIdOverride = "") => {
      const providerSessionId =
        providerSessionIdOverride ||
        variables.ProviderSessionId ||
        sessionForm.providerSessionId.trim();

      return {
        ...variables,
        ...(providerSessionId ? { ProviderSessionId: providerSessionId } : {}),
      };
    },
    [sessionForm.providerSessionId],
  );

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    persistSessionDraft(sessionForm);
  }, [sessionForm]);

  useEffect(() => {
    persistConversationKeys(conversationKeysById);
  }, [conversationKeysById]);

  const persistedSessionDebug = (() => {
    if (typeof window === "undefined") {
      return {
        hasSavedSession: false,
        savedSessionText: "Local storage unavailable.",
      };
    }

    const savedSession =
      window.localStorage.getItem(CNX_SESSION_STORAGE_KEY) ||
      window.localStorage.getItem(LEGACY_CNX_SESSION_STORAGE_KEY);

    return {
      hasSavedSession: Boolean(savedSession),
      savedSessionText: savedSession || "No saved CNX session found.",
    };
  })();

  const selectedConversation = useMemo(
    () =>
      conversations.find(
        (conversation) => conversation.id === selectedConversationId,
      ) || null,
    [conversations, selectedConversationId],
  );
  const selectedConversationKey = selectedConversation
    ? conversationKeysById[selectedConversation.id] || ""
    : "";
  const selectedConversationCryptoStatus = selectedConversation
    ? conversationCryptoStatusById[selectedConversation.id] || "idle"
    : "idle";
  const authenticatedPointer =
    activationSupport.cnxPointer.trim() ||
    debugState.normalizedResponse?.prefill?.suggested_cnx_pointer ||
    "";

  useEffect(() => {
    if (!selectedConversation) {
      setConversationTitleDraft("");
      setIsEditingConversationTitle(false);
      return;
    }

    setConversationTitleDraft(selectedConversation.title || "");
  }, [selectedConversation]);

  const setSelectedConversationKey = (nextValue) => {
    setConversationKeysById((current) => {
      if (!selectedConversation?.id) {
        return current;
      }

      const trimmedValue = String(nextValue || "").trim();

      if (!trimmedValue) {
        const nextKeys = { ...current };
        delete nextKeys[selectedConversation.id];
        return nextKeys;
      }

      return {
        ...current,
        [selectedConversation.id]: trimmedValue,
      };
    });
  };

  const ensureConversationKeyForSelectedConversation = async () => {
    if (!selectedConversation || selectedConversation.source !== "cnx") {
      throw new Error(
        "Open a real CNX conversation before preparing encryption.",
      );
    }

    const existingConversationKey = String(
      conversationKeysById[selectedConversation.id] || "",
    ).trim();

    if (existingConversationKey) {
      return existingConversationKey;
    }

    const nextConversationKey = await generateSharedConversationKeyBase64();

    setConversationKeysById((current) => ({
      ...current,
      [selectedConversation.id]: nextConversationKey,
    }));
    appendActivity(
      `Generated a local proof room key for ${selectedConversation.title}. Keep this aligned across test browsers until wrapped key distribution lands.`,
    );

    try {
      await publishConversationKeyBox({
        conversationId: selectedConversation.id,
        conversationKeyBase64: nextConversationKey,
      });
      appendActivity(
        `Published proof conversation crypto metadata for ${selectedConversation.title}.`,
      );
    } catch (error) {
      setConversationCryptoStatusById((current) => ({
        ...current,
        [selectedConversation.id]: "publish_failed",
      }));
      appendActivity(
        `Generated the local proof room key, but could not publish conversation crypto yet: ${error.message}`,
      );
    }

    return nextConversationKey;
  };

  const publishConversationKeyBox = async ({
    conversationId,
    conversationKeyBase64,
  }) => {
    const trimmedConversationId = String(conversationId || "").trim();
    const trimmedConversationKey = String(conversationKeyBase64 || "").trim();

    if (!trimmedConversationId || !trimmedConversationKey) {
      return null;
    }

    const backendResponse = await serviceGraphCall(
      "mutation",
      "cnxManager",
      buildCnxManagerVariables({
        Action: "publishConversationKeyBox",
        ConversationId: trimmedConversationId,
        WrappedKeyEnvelope: JSON.stringify(
          buildProofWrappedConversationKeyEnvelope(trimmedConversationKey),
        ),
        ConversationKeyRef: createConversationKeyRef(trimmedConversationId),
        Algorithm: "cnx.signal-family.v1",
        KeyDerivationScheme: "proof-room-key",
      }),
    );
    const normalizedBackendResponse =
      normalizeConversationCryptoResponse(backendResponse);

    if (!normalizedBackendResponse) {
      throw new Error(
        "Provider backend did not return valid conversation crypto material.",
      );
    }

    setConversationCryptoStatusById((current) => ({
      ...current,
      [trimmedConversationId]: "published",
    }));

    return normalizedBackendResponse;
  };

  const restoreConversationKeyFromBackend = useCallback(
    async (conversationId) => {
      const trimmedConversationId = String(conversationId || "").trim();

      if (!trimmedConversationId) {
        return "";
      }

      const backendResponse = await serviceGraphCall(
        "mutation",
        "cnxManager",
        buildCnxManagerVariables({
          Action: "getConversationCrypto",
          ConversationId: trimmedConversationId,
        }),
      );
      const normalizedBackendResponse =
        normalizeConversationCryptoResponse(backendResponse);

      if (!normalizedBackendResponse) {
        setConversationCryptoStatusById((current) => ({
          ...current,
          [trimmedConversationId]: "missing",
        }));
        return "";
      }

      const restoredConversationKey = readConversationKeyFromWrappedEnvelope(
        normalizedBackendResponse.keyBox?.wrappedKeyEnvelope,
      );

      if (!restoredConversationKey) {
        setConversationCryptoStatusById((current) => ({
          ...current,
          [trimmedConversationId]: "missing",
        }));
        return "";
      }

      setConversationKeysById((current) => ({
        ...current,
        [trimmedConversationId]: restoredConversationKey,
      }));
      setConversationCryptoStatusById((current) => ({
        ...current,
        [trimmedConversationId]: "restored",
      }));

      return restoredConversationKey;
    },
    [buildCnxManagerVariables],
  );

  const resetSessionState = useCallback(
    ({
      clearPersistedDraft = false,
      preserveBaseUrl = true,
      preserveActivity = false,
    } = {}) => {
      const nextBootstrapDraft = buildBootstrapDraft(user);
      const nextActivationSupport = buildActivationSupportDraft(user);
      const nextSessionDraft = {
        baseUrl: preserveBaseUrl
          ? normalizeSessionBaseUrl(sessionForm.baseUrl)
          : DEFAULT_CNX_BASE_URL,
        providerSessionId: "",
        expiresAtEpochSeconds: "",
        accountId: "",
      };

      setSessionForm(nextSessionDraft);
      if (clearPersistedDraft) {
        clearPersistedSessionDraft();
      } else {
        persistSessionDraft(nextSessionDraft);
      }
      setSessionReady(false);
      setBootstrapOutcome("Awaiting provider bootstrap");
      setConversations(buildInitialConversations());
      setSelectedConversationId("");
      setActiveChatView("conversations");
      setRecipientPointer("");
      setMemberPointer("");
      setMessageBody("");
      setCiphertext("");
      setNonce("");
      setConversationCryptoStatusById({});
      setChatNotice(null);
      setLoadingAction("");
      setDebugDrawerOpen(false);
      setBootstrapDraft(nextBootstrapDraft);
      setActivationSupport(nextActivationSupport);
      setDebugState(resetBootstrapDebugState());
      setSteps(buildInitialSteps(user));

      if (!preserveActivity) {
        setActivity([]);
      }

      return nextSessionDraft;
    },
    [sessionForm.baseUrl, user],
  );

  const updateStep = useCallback((id, status) => {
    setSteps((current) =>
      current.map((step) => (step.id === id ? { ...step, status } : step)),
    );
  }, []);

  const appendActivity = useCallback((line) => {
    setActivity((current) => [line, ...current].slice(0, 10));
  }, []);

  const showChatNotice = (severity, message) => {
    if (!message) {
      return;
    }

    setChatNotice({
      severity,
      message,
    });
  };

  const recordDebugState = useCallback(
    ({
      action,
      rawResponse = null,
      normalizedResponse = null,
      lastError = "",
    }) => {
      setDebugState((current) => ({
        ...current,
        lastAction: action,
        rawResponse,
        normalizedResponse,
        lastError,
      }));
    },
    [],
  );

  const recordConversationLoadState = useCallback(
    ({
      status,
      rawResponse = null,
      normalizedResponse = null,
      lastError = "",
    }) => {
      setDebugState((current) => ({
        ...current,
        conversationLoad: {
          status,
          rawResponse,
          normalizedResponse,
          lastError,
        },
      }));
    },
    [],
  );

  const applyChallengeState = (result) => {
    if (!result || !result.challenge) {
      return;
    }

    setActivationSupport((current) => ({
      ...current,
      challengeId: result.challenge.challenge_id || "",
      challengeType: result.challenge.challenge_type || "",
      verificationCode: "",
      methods: Array.isArray(result.challenge.methods)
        ? result.challenge.methods
        : [],
      publicKey: result.challenge.public_key
        ? JSON.stringify(result.challenge.public_key, null, 2)
        : "",
      credential: "",
      verificationSource: "createSession",
    }));
  };

  const handleRegistrationStep = async () => {
    await runWithLoadingAction("registerUser", async () => {
      try {
        const response = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "registerUser",
            SuggestedCnxPointer:
              activationSupport.cnxPointer.trim() || undefined,
            ChallengeId: activationSupport.challengeId || undefined,
            VerificationCode: activationSupport.verificationCode || undefined,
          }),
        );

        const result = normalizeActivationActionResponse(response, {
          status: "link_required",
          message:
            "CNX registration step completed. The next step is to create or confirm the Entzy provider link.",
        });
        recordDebugState({
          action: "registerUser",
          rawResponse: response,
          normalizedResponse: result,
        });

        if (
          await finalizeSessionReadyResult(
            result,
            "Completed the CNX registration step and continued directly into chat.",
            readProviderSessionIdFromPayload(result),
          )
        ) {
          return;
        }

        applyChallengeState(result);
        setActivationSupport((current) => ({
          ...current,
          verificationSource:
            result.status === "verification_required"
              ? "registerUser"
              : current.verificationSource,
        }));

        setBootstrapOutcome(result.status || "link_required");
        updateStep("session", "pending");
        appendActivity(
          result.message ||
            "Completed the CNX registration step and moved the flow into provider linking.",
        );
      } catch (error) {
        setBootstrapOutcome("registration_required");
        recordDebugState({
          action: "registerUser",
          lastError: error.message,
        });
        appendActivity(
          `Unable to complete the CNX registration step yet: ${error.message}`,
        );
      }
    });
  };

  const handleSessionVerificationStep = async () => {
    await runWithLoadingAction("verifySession", async () => {
      try {
        const response = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "createSession",
            ChallengeId:
              activationSupport.challengeId &&
              activationSupport.verificationCode
                ? activationSupport.challengeId
                : undefined,
            VerificationCode:
              activationSupport.challengeId &&
              activationSupport.verificationCode
                ? activationSupport.verificationCode
                : undefined,
          }),
        );
        const result = mapBootstrapGraphResponse(response, bootstrapDraft);
        recordDebugState({
          action: "createSession",
          rawResponse: response,
          normalizedResponse: result,
        });

        if (
          await finalizeSessionReadyResult(
            result,
            "Chat activated. CNX session is ready and the next step is to continue directly into the DM conversation bootstrap.",
            readProviderSessionIdFromPayload(result),
          )
        ) {
          return;
        }

        applyChallengeState(result);
        setActivationSupport((current) => ({
          ...current,
          verificationSource: "createSession",
        }));

        setSessionReady(false);
        setBootstrapOutcome(resolveActivationStatus(result.status));
        updateStep("session", "pending");
        appendActivity(
          isChallengeRestartRequired(result.message)
            ? "That verification challenge is no longer active. Send a fresh code and try again."
            : result.message ||
                `Bootstrap returned ${result.status}. The activation flow should now continue through the next CNX registration or linking step.`,
        );
      } catch (error) {
        recordDebugState({
          action: "createSession",
          lastError: error.message,
        });
        setSessionReady(false);
        updateStep("session", "pending");
        if (isRecoverableVerificationError(error.message)) {
          setActivationSupport((current) => ({
            ...current,
            verificationSource: "createSession",
          }));
          setBootstrapOutcome("verification_required");
          appendActivity(
            `The final CNX verification code was not accepted yet: ${error.message}`,
          );
        } else {
          appendActivity(
            `Unable to verify the CNX session step: ${error.message}`,
          );
        }
      }
    });
  };

  const handleResendVerificationCode = async () => {
    await runWithLoadingAction("resendVerification", async () => {
      try {
        if (verificationIsLogin) {
          const response = await serviceGraphCall(
            "mutation",
            "cnxManager",
            buildCnxManagerVariables({
              Action: "createSession",
            }),
          );
          const result = mapBootstrapGraphResponse(response, bootstrapDraft);
          recordDebugState({
            action: "createSession",
            rawResponse: response,
            normalizedResponse: result,
          });

          if (
            await finalizeSessionReadyResult(
              result,
              "A fresh login step was not needed because CNX restored the session directly.",
              readProviderSessionIdFromPayload(result),
            )
          ) {
            return;
          }

          applyChallengeState(result);
          setActivationSupport((current) => ({
            ...current,
            verificationCode: "",
            verificationSource: "createSession",
          }));
          setSessionReady(false);
          setBootstrapOutcome(result.status || "verification_required");
          updateStep("session", "pending");
          appendActivity(
            result.message ||
              "Sent a fresh CNX login verification code. Enter the new code to continue.",
          );
          return;
        }

        const response = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "registerUser",
            SuggestedCnxPointer:
              activationSupport.cnxPointer.trim() || undefined,
          }),
        );
        const result = normalizeActivationActionResponse(response, {
          status: "verification_required",
          message:
            "Sent a fresh CNX registration verification code. Enter the new code to continue.",
        });
        recordDebugState({
          action: "registerUser",
          rawResponse: response,
          normalizedResponse: result,
        });
        applyChallengeState(result);
        setActivationSupport((current) => ({
          ...current,
          verificationCode: "",
          verificationSource: "registerUser",
        }));
        setBootstrapOutcome(result.status || "verification_required");
        updateStep("session", "pending");
        appendActivity(
          result.message ||
            "Sent a fresh CNX registration verification code. Enter the new code to continue.",
        );
      } catch (error) {
        appendActivity(
          `Unable to send a fresh verification code yet: ${error.message}`,
        );
      }
    });
  };

  const handleProviderLinkStep = async () => {
    await runWithLoadingAction("confirmProviderLink", async () => {
      try {
        const response = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "confirmProviderLink",
          }),
        );

        const linkResult = normalizeActivationActionResponse(response, {
          status: "Awaiting provider bootstrap",
          message:
            "Your new CNX connection point is ready. Continuing now to connect it to Entzy Chat.",
        });
        recordDebugState({
          action: "confirmProviderLink",
          rawResponse: response,
          normalizedResponse: linkResult,
        });

        appendActivity(
          linkResult.message ||
            "Your new CNX connection point is ready. Continuing now to connect it to Entzy Chat.",
        );

        const createSessionResponse = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "createSession",
          }),
        );
        const result = mapBootstrapGraphResponse(
          createSessionResponse,
          bootstrapDraft,
        );
        recordDebugState({
          action: "createSession",
          rawResponse: createSessionResponse,
          normalizedResponse: result,
        });

        if (
          await finalizeSessionReadyResult(
            result,
            "Connected the new CNX connection point to Entzy Chat and continued into the DM flow.",
            readProviderSessionIdFromPayload(result),
          )
        ) {
          return;
        }

        applyChallengeState(result);
        setActivationSupport((current) => ({
          ...current,
          verificationSource: "createSession",
        }));
        setSessionReady(false);
        setBootstrapOutcome(resolveActivationStatus(result.status));
        updateStep("session", "pending");
        appendActivity(
          result.message ||
            "Your new CNX connection point is ready. Enter the final verification code to connect it to Entzy Chat.",
        );
      } catch (error) {
        recordDebugState({
          action: "confirmProviderLink",
          lastError: error.message,
        });
        setSessionReady(false);
        updateStep("session", "pending");
        if (isRecoverableVerificationError(error.message)) {
          setActivationSupport((current) => ({
            ...current,
            verificationSource: "createSession",
          }));
          setBootstrapOutcome("verification_required");
          appendActivity(
            `The final CNX verification code was not accepted yet: ${error.message}`,
          );
        } else {
          appendActivity(
            `Unable to complete the provider link step yet: ${error.message}`,
          );
        }
      }
    });
  };

  const handleRefreshSession = async () => {
    await runWithLoadingAction("refreshSession", async () => {
      try {
        const response = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "refreshSession",
          }),
        );

        const result = normalizeActivationActionResponse(response, {
          status: "verification_required",
          message:
            "CNX session refresh needs another verification step before chat can continue.",
        });

        recordDebugState({
          action: "refreshSession",
          rawResponse: response,
          normalizedResponse: result,
        });

        if (
          await finalizeSessionReadyResult(
            result,
            "Refreshed the CNX session and kept the chat room active.",
          )
        ) {
          return;
        }

        applyChallengeState(result);

        setSessionForm((current) => ({
          ...current,
          expiresAtEpochSeconds: "",
        }));
        setSessionReady(false);
        setBootstrapOutcome(result.status || "verification_required");
        updateStep("session", "pending");
        appendActivity(
          result.message ||
            "CNX session refresh needs another verification step before chat can continue.",
        );
      } catch (error) {
        recordDebugState({
          action: "refreshSession",
          lastError: error.message,
        });
        appendActivity(`Unable to refresh the CNX session: ${error.message}`);
      }
    });
  };

  const handleDisconnect = () => {
    setDisconnectDialogOpen(true);
  };

  const handleConfirmDisconnect = () => {
    setDisconnectDialogOpen(false);
    setActiveChatView("conversations");
    resetSessionState();
    appendActivity(
      "Disconnected the CNX session and reset the chat activation state.",
    );
  };

  const finalizeSessionReadyResult = async (
    result,
    successMessage,
    fallbackProviderSessionId = "",
  ) => {
    const session = result?.session || {};
    const providerSessionId =
      readProviderSessionIdFromPayload(result) ||
      fallbackProviderSessionId ||
      sessionForm.providerSessionId.trim();
    const accountId =
      session.cnx_user_id ||
      session.cnxUserId ||
      session.account_id ||
      session.accountId ||
      sessionForm.accountId.trim() ||
      "";

    if (!providerSessionId) {
      return false;
    }

    const nextSession = {
      providerSessionId,
      expiresAtEpochSeconds:
        session.expires_at_epoch_seconds || session.expiresAtEpochSeconds || "",
      accountId,
    };

    setSessionForm((current) => {
      const persistedSession = {
        ...current,
        providerSessionId: nextSession.providerSessionId,
        expiresAtEpochSeconds: nextSession.expiresAtEpochSeconds,
        accountId: nextSession.accountId,
      };
      persistSessionDraft(persistedSession);
      return persistedSession;
    });
    setActivationSupport((current) => ({
      ...current,
      cnxPointer:
        result?.prefill?.suggested_cnx_pointer || current.cnxPointer || "",
      challengeId: "",
      challengeType: "",
      verificationCode: "",
      methods: [],
      publicKey: "",
      credential: "",
      verificationSource: "createSession",
    }));
    setSessionReady(true);
    setActiveChatView("conversations");
    setBootstrapOutcome("session_ready");
    updateStep("session", "ready");
    appendActivity(successMessage);

    try {
      await loadConversations(nextSession.providerSessionId);
    } catch (error) {
      appendActivity(
        `CNX session is ready, but the initial conversation sync failed: ${error.message}`,
      );
    }

    return true;
  };

  const handleVerifyMfaTotp = async () => {
    await runWithLoadingAction("verifyMfaTotp", async () => {
      try {
        const response = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "verifyMfaTotp",
            ChallengeId: activationSupport.challengeId || undefined,
            VerificationCode: activationSupport.verificationCode || undefined,
          }),
        );

        const result = normalizeActivationActionResponse(response, {
          status: "mfa_required",
          message:
            "CNX still needs a valid authenticator code before chat can activate.",
        });

        recordDebugState({
          action: "verifyMfaTotp",
          rawResponse: response,
          normalizedResponse: result,
        });

        if (
          await finalizeSessionReadyResult(
            result,
            "Verified CNX authenticator MFA and continued into chat.",
          )
        ) {
          return;
        }

        applyChallengeState(result);
        setSessionReady(false);
        setBootstrapOutcome(result.status || "mfa_required");
        updateStep("session", "pending");
        appendActivity(
          result.message ||
            "CNX still needs a valid authenticator code before chat can activate.",
        );
      } catch (error) {
        recordDebugState({
          action: "verifyMfaTotp",
          lastError: error.message,
        });
        setSessionReady(false);
        setBootstrapOutcome("mfa_required");
        updateStep("session", "pending");
        appendActivity(`Unable to verify CNX MFA yet: ${error.message}`);
      }
    });
  };

  const handleStartMfaPasskey = async () => {
    await runWithLoadingAction("startMfaPasskey", async () => {
      let popupWindow = null;

      try {
        setPasskeyOverlayMessage("Opening the secure CNX passkey check.");
        setPasskeyOverlayOpen(true);
        popupWindow = openPasskeyPopup(cnxSiteUrl);

        if (!popupWindow) {
          throw new Error(
            "Passkey window was blocked. Allow popups for this site and try again.",
          );
        }

        const response = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "startMfaPasskey",
            ChallengeId: activationSupport.challengeId || undefined,
          }),
        );

        const result = normalizeActivationActionResponse(response, {
          status: "mfa_required",
          message: "CNX returned a passkey challenge for the linked user.",
        });

        recordDebugState({
          action: "startMfaPasskey",
          rawResponse: response,
          normalizedResponse: result,
        });

        applyChallengeState(result);
        setSessionReady(false);
        setBootstrapOutcome(result.status || "mfa_required");
        updateStep("session", "pending");
        appendActivity(
          result.message ||
            "CNX returned a passkey challenge for the linked user.",
        );

        const popupOrigin = new URL(cnxSiteUrl).origin;
        setPasskeyOverlayMessage(
          "Waiting for the secure passkey window to get ready.",
        );
        await waitForPasskeyPopupReady(popupWindow, popupOrigin);
        const initMessage = {
          type: "cnx:passkey:init",
          challengeId:
            activationSupport.challengeId || result.challenge?.challenge_id,
          publicKey: result.challenge?.public_key,
        };
        popupWindow.postMessage(initMessage, popupOrigin);

        setPasskeyOverlayMessage(
          "Complete the passkey check in the secure CNX window.",
        );
        const serializedCredential = await waitForPasskeyPopupResult(
          popupWindow,
          popupOrigin,
          initMessage.challengeId,
        );

        setActivationSupport((current) => ({
          ...current,
          credential: serializedCredential,
        }));
        appendActivity(
          "Passkey assertion returned from CNX and ready for verification.",
        );
        setPasskeyOverlayMessage(
          "Passkey received. Finishing verification with Entzy.",
        );
        const verifySucceeded =
          await handleVerifyMfaPasskey(serializedCredential);
        notifyPasskeyPopupCompletion(
          popupWindow,
          cnxSiteUrl,
          verifySucceeded
            ? "Passkey verified. Returning to Entzy chat."
            : "Passkey captured, but Entzy still needs another verification step.",
        );
        setPasskeyOverlayOpen(false);
      } catch (error) {
        recordDebugState({
          action: "startMfaPasskey",
          lastError: error.message,
        });
        setSessionReady(false);
        setBootstrapOutcome("mfa_required");
        updateStep("session", "pending");
        appendActivity(`Unable to start passkey MFA yet: ${error.message}`);
        setPasskeyOverlayMessage(error.message);
        if (popupWindow && !popupWindow.closed) {
          notifyPasskeyPopupCompletion(
            popupWindow,
            cnxSiteUrl,
            `Passkey verification did not complete: ${error.message}`,
          );
        }
        setPasskeyOverlayOpen(false);
      }
    });
  };

  const handleVerifyMfaPasskey = async (credentialOverride = "") => {
    return runWithLoadingAction("verifyMfaPasskey", async () => {
      try {
        const credentialPayload =
          typeof credentialOverride === "string" && credentialOverride
            ? credentialOverride
            : activationSupport.credential.trim();

        if (!credentialPayload) {
          appendActivity(
            "Start passkey MFA first so the browser can capture a credential assertion.",
          );
          return;
        }

        const response = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "verifyMfaPasskey",
            ChallengeId: activationSupport.challengeId || undefined,
            Credential: credentialPayload || undefined,
          }),
        );

        const result = normalizeActivationActionResponse(response, {
          status: "mfa_required",
          message:
            "CNX still needs a valid passkey assertion before chat can activate.",
        });

        recordDebugState({
          action: "verifyMfaPasskey",
          rawResponse: response,
          normalizedResponse: result,
        });

        if (
          await finalizeSessionReadyResult(
            result,
            "Verified CNX passkey MFA and continued into chat.",
          )
        ) {
          return true;
        }

        applyChallengeState(result);
        setSessionReady(false);
        setBootstrapOutcome(result.status || "mfa_required");
        updateStep("session", "pending");
        appendActivity(
          result.message ||
            "CNX still needs a valid passkey assertion before chat can activate.",
        );
        if (result.status && result.status !== "session_ready") {
          appendActivity(`Passkey verify returned ${result.status}.`);
        }
        return false;
      } catch (error) {
        recordDebugState({
          action: "verifyMfaPasskey",
          lastError: error.message,
        });
        setSessionReady(false);
        setBootstrapOutcome("mfa_required");
        updateStep("session", "pending");
        appendActivity(`Unable to verify passkey MFA yet: ${error.message}`);
        return false;
      }
    });
  };

  const runWithLoadingAction = useCallback(async (action, callback) => {
    setLoadingAction(action);
    try {
      return await callback();
    } finally {
      setLoadingAction((current) => (current === action ? "" : current));
    }
  }, []);

  const refreshConversationMessages = useCallback(
    async (conversationId, conversationKeyOverride = "") => {
      const backendResponse = await serviceGraphCall(
        "mutation",
        "cnxManager",
        buildCnxManagerVariables({
          Action: "syncThread",
          ConversationId: conversationId,
        }),
      );

      const normalizedBackendResponse =
        normalizeSyncThreadResponse(backendResponse);

      if (normalizedBackendResponse) {
        const hydratedMessages = await Promise.all(
          normalizedBackendResponse.messages.map((item) =>
            mapChatMessageResultToMessage(
              item,
              sessionForm.accountId.trim(),
              conversationKeyOverride ||
                conversationKeysById[conversationId] ||
                "",
            ),
          ),
        );

        setConversations((current) =>
          current.map((conversation) =>
            conversation.id === conversationId
              ? {
                  ...conversation,
                  source: "cnx",
                  messages: hydratedMessages,
                  messagesLoaded: true,
                }
              : conversation,
          ),
        );
        appendActivity(
          "Synced encrypted messages through the provider backend.",
        );
        return;
      }

      throw new Error(
        "Provider backend did not return a valid syncThread payload.",
      );
    },
    [
      appendActivity,
      buildCnxManagerVariables,
      conversationKeysById,
      sessionForm.accountId,
    ],
  );

  const loadConversations = useCallback(
    async (providerSessionIdOverride = "") => {
      return runWithLoadingAction("listConversations", async () => {
        const backendResponse = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables(
            {
              Action: "listConversations",
            },
            providerSessionIdOverride,
          ),
        );

        const normalizedBackendResponse =
          normalizeListConversationsResponse(backendResponse);

        if (normalizedBackendResponse) {
          recordConversationLoadState({
            status: normalizedBackendResponse.conversations.length
              ? "ready"
              : "empty",
            rawResponse: backendResponse,
            normalizedResponse: normalizedBackendResponse,
          });
          if (!normalizedBackendResponse.conversations.length) {
            setConversations(buildInitialConversations());
            setSelectedConversationId("");
            appendActivity(
              "CNX session connected. No conversations were returned yet.",
            );
            return;
          }

          setConversations((current) =>
            normalizedBackendResponse.conversations.map((summary) =>
              mergeConversationSummaryWithExisting(
                mapConversationSummary(summary),
                current.find(
                  (conversation) => conversation.id === summary.conversation.id,
                ),
              ),
            ),
          );
          setSelectedConversationId((current) =>
            normalizedBackendResponse.conversations.some(
              (summary) => summary.conversation.id === current,
            )
              ? current
              : "",
          );
          appendActivity(
            `Loaded ${normalizedBackendResponse.conversations.length} conversation(s) from CNX via provider backend.`,
          );
          return;
        }

        const errorText = [
          backendResponse?.message,
          backendResponse?.data?.message,
          backendResponse?.data?.errors
            ? JSON.stringify(backendResponse.data.errors)
            : null,
        ]
          .filter(Boolean)
          .join(" ");

        recordConversationLoadState({
          status: "error",
          rawResponse: backendResponse,
          normalizedResponse: normalizedBackendResponse,
          lastError:
            errorText ||
            "Provider backend did not return a valid listConversations payload.",
        });

        throw new Error(
          errorText ||
            "Provider backend did not return a valid listConversations payload.",
        );
      });
    },
    [
      appendActivity,
      buildCnxManagerVariables,
      recordConversationLoadState,
      runWithLoadingAction,
    ],
  );

  const handlePrepare = async () => {
    if (!user || !user.connected) {
      setSessionReady(false);
      updateStep("session", "blocked");
      appendActivity("Connect to Entzy first before attempting the CNX proof.");
      return;
    }

    await runWithLoadingAction("prepareSession", async () => {
      try {
        const response = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "createSession",
          }),
        );
        const result = mapBootstrapGraphResponse(response, bootstrapDraft);
        recordDebugState({
          action: "createSession",
          rawResponse: response,
          normalizedResponse: result,
        });

        if (
          await finalizeSessionReadyResult(
            result,
            "Chat activated. CNX session is ready and the next step is to continue directly into the DM conversation bootstrap.",
            readProviderSessionIdFromPayload(result),
          )
        ) {
          return;
        }

        applyChallengeState(result);

        setSessionReady(false);
        setBootstrapOutcome(resolveActivationStatus(result.status));
        updateStep("session", "pending");
        appendActivity(
          result.message ||
            `Bootstrap returned ${result.status}. The activation flow should now continue through the next CNX registration or linking step.`,
        );
      } catch (error) {
        recordDebugState({
          action: "createSession",
          lastError: error.message,
        });
        setSessionReady(false);
        updateStep("session", "pending");
        appendActivity(`Unable to prepare CNX session: ${error.message}`);
      }
    });
  };

  const handleCreateConversation = async () => {
    const trimmedRecipient = recipientPointer.trim();
    const trimmedRoomTitle = trimmedRecipient;

    if (!sessionReady) {
      appendActivity("Prepare the CNX session before creating a room.");
      return;
    }

    await runWithLoadingAction("createConversation", async () => {
      try {
        const backendResponse = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "createConversation",
            ConversationTitle: trimmedRoomTitle || undefined,
          }),
        );

        const normalizedBackendResponse =
          normalizeCreateConversationResponse(backendResponse);

        if (
          normalizedBackendResponse?.status === "ok" &&
          normalizedBackendResponse.conversations.length
        ) {
          const conversationSummary =
            normalizedBackendResponse.conversations[0];
          const mappedConversation =
            mapConversationSummary(conversationSummary);
          const initialMessages = await Promise.all(
            normalizedBackendResponse.messages.map((item) =>
              mapChatMessageResultToMessage(
                item,
                sessionForm.accountId.trim(),
                conversationKeysById[mappedConversation.id] || "",
              ),
            ),
          );
          const nextConversation = {
            ...mappedConversation,
            title:
              trimmedRoomTitle ||
              mappedConversation.title ||
              buildDefaultRoomTitle(),
            recipientPointer: "",
            messages: initialMessages,
            messagesLoaded: true,
          };

          setConversations((current) => {
            const withoutExisting = current.filter(
              (item) => item.id !== nextConversation.id,
            );
            return [nextConversation, ...withoutExisting];
          });
          setSelectedConversationId(nextConversation.id);
          setActiveChatView("conversations");
          setRecipientPointer("");
          updateStep("conversation", "ready");
          appendActivity(
            trimmedRoomTitle
              ? `Created chat room ${trimmedRoomTitle} through the provider backend.`
              : `Created chat room ${nextConversation.id} through the provider backend.`,
          );
          return;
        }

        throw new Error(
          "Provider backend did not return a valid createConversation payload.",
        );
      } catch (error) {
        appendActivity(
          `Unable to create or reuse direct conversation: ${error.message}`,
        );
      }
    });
  };

  const loadConversationParticipants = useCallback(
    async (conversationId) => {
      const backendResponse = await serviceGraphCall(
        "mutation",
        "cnxManager",
        buildCnxManagerVariables({
          Action: "listParticipants",
          ConversationId: conversationId,
        }),
      );

      const normalizedBackendResponse =
        normalizeListParticipantsResponse(backendResponse);

      if (normalizedBackendResponse?.status === "ok") {
        const participants = normalizedBackendResponse.participants
          .map(mapConversationParticipant)
          .filter(Boolean);

        setConversations((current) =>
          current.map((conversation) =>
            conversation.id === conversationId
              ? {
                  ...conversation,
                  participants,
                  participantsLoaded: true,
                  participantCount:
                    participants.length || conversation.participantCount || 0,
                }
              : conversation,
          ),
        );
        return;
      }

      throw new Error(
        "Provider backend did not return a valid listParticipants payload.",
      );
    },
    [buildCnxManagerVariables],
  );

  const handleAddParticipant = async () => {
    if (!sessionReady) {
      appendActivity("Prepare the CNX session before adding a room member.");
      return;
    }

    if (!selectedConversation || selectedConversation.source !== "cnx") {
      appendActivity(
        "Open a real CNX conversation before adding a room member.",
      );
      return;
    }

    const trimmedMemberPointer = memberPointer.trim();

    if (!trimmedMemberPointer) {
      appendActivity("Enter a CNX pointer before adding a room member.");
      return;
    }

    await runWithLoadingAction("addParticipants", async () => {
      try {
        const backendResponse = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "addParticipants",
            ConversationId: selectedConversation.id,
            SuggestedCnxPointer: trimmedMemberPointer,
          }),
        );

        const normalizedBackendResponse =
          normalizeAddParticipantsResponse(backendResponse);

        if (
          normalizedBackendResponse?.status === "ok" &&
          normalizedBackendResponse.conversations.length
        ) {
          const nextConversation = {
            ...selectedConversation,
            ...mapConversationSummary(
              normalizedBackendResponse.conversations[0],
            ),
          };

          setConversations((current) =>
            current.map((conversation) =>
              conversation.id === nextConversation.id
                ? {
                    ...conversation,
                    ...nextConversation,
                  }
                : conversation,
            ),
          );
          setMemberPointer("");
          updateStep("conversation", "ready");
          appendActivity(
            `Added ${trimmedMemberPointer} to ${nextConversation.title}.`,
          );
          showChatNotice(
            "success",
            `Added ${trimmedMemberPointer} to ${nextConversation.title}.`,
          );
          try {
            await loadConversationParticipants(nextConversation.id);
          } catch (participantError) {
            appendActivity(
              `Member was added, but the participant list could not be refreshed yet: ${participantError.message}`,
            );
          }
          return;
        }

        if (normalizedBackendResponse) {
          const fallbackMessage =
            normalizedBackendResponse.message ||
            (normalizedBackendResponse.status === "not_found"
              ? `No CNX user was found for ${trimmedMemberPointer}.`
              : "Unable to add that room member yet.");
          appendActivity(fallbackMessage);
          showChatNotice(
            normalizedBackendResponse.status === "not_found"
              ? "error"
              : "warning",
            fallbackMessage,
          );
          return;
        }

        throw new Error(
          "Provider backend did not return a valid addParticipants payload.",
        );
      } catch (error) {
        appendActivity(`Unable to add room member: ${error.message}`);
      }
    });
  };

  const handleRemoveParticipant = async (participant) => {
    if (!sessionReady) {
      appendActivity("Prepare the CNX session before removing a room member.");
      return;
    }

    if (!selectedConversation || selectedConversation.source !== "cnx") {
      appendActivity(
        "Open a real CNX conversation before removing a room member.",
      );
      return;
    }

    const participantId = String(participant?.id || "").trim();
    const participantLabel =
      participant?.pointer || participant?.name || participantId;

    if (!participantId) {
      appendActivity(
        "This room member cannot be removed yet because no participant id was returned.",
      );
      showChatNotice(
        "warning",
        "This room member cannot be removed yet because no participant id was returned.",
      );
      return;
    }

    await runWithLoadingAction("removeParticipants", async () => {
      try {
        const backendResponse = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "removeParticipants",
            ConversationId: selectedConversation.id,
            ParticipantAccountIds: [participantId],
          }),
        );

        const normalizedBackendResponse =
          normalizeRemoveParticipantsResponse(backendResponse);

        if (
          normalizedBackendResponse?.status === "ok" &&
          normalizedBackendResponse.conversations.length
        ) {
          const nextConversation = {
            ...selectedConversation,
            ...mapConversationSummary(
              normalizedBackendResponse.conversations[0],
            ),
          };

          setConversations((current) =>
            current.map((conversation) => {
              const nextParticipants = (conversation.participants || []).filter(
                (currentParticipant) => currentParticipant.id !== participantId,
              );

              return conversation.id === nextConversation.id
                ? {
                    ...conversation,
                    ...nextConversation,
                    participants: nextParticipants,
                    participantsLoaded: true,
                    participantCount:
                      nextParticipants.length ||
                      nextConversation.participantCount ||
                      0,
                  }
                : conversation;
            }),
          );
          appendActivity(
            `Removed ${participantLabel} from ${nextConversation.title}.`,
          );
          showChatNotice(
            "success",
            `Removed ${participantLabel} from ${nextConversation.title}.`,
          );
          try {
            await loadConversationParticipants(nextConversation.id);
          } catch (participantError) {
            appendActivity(
              `Member was removed, but the participant list could not be refreshed yet: ${participantError.message}`,
            );
          }
          return;
        }

        if (normalizedBackendResponse) {
          const fallbackMessage =
            normalizedBackendResponse.message ||
            "Unable to remove that room member yet.";
          appendActivity(fallbackMessage);
          showChatNotice("warning", fallbackMessage);
          return;
        }

        throw new Error(
          "Provider backend did not return a valid removeParticipants payload.",
        );
      } catch (error) {
        appendActivity(`Unable to remove room member: ${error.message}`);
        showChatNotice(
          "error",
          `Unable to remove room member: ${error.message}`,
        );
      }
    });
  };

  const handleGenerateConversationKey = async () => {
    if (!selectedConversation || selectedConversation.source !== "cnx") {
      appendActivity(
        "Open a real CNX conversation before generating a room key.",
      );
      return;
    }

    try {
      const nextConversationKey = await generateSharedConversationKeyBase64();

      setConversationKeysById((current) => ({
        ...current,
        [selectedConversation.id]: nextConversationKey,
      }));
      try {
        await publishConversationKeyBox({
          conversationId: selectedConversation.id,
          conversationKeyBase64: nextConversationKey,
        });
      } catch (publishError) {
        setConversationCryptoStatusById((current) => ({
          ...current,
          [selectedConversation.id]: "publish_failed",
        }));
        appendActivity(
          `Generated the room key but could not publish conversation crypto yet: ${publishError.message}`,
        );
      }
      appendActivity(
        `Generated a shared room key for ${selectedConversation.title}. Keep this proof-only key aligned across test participants.`,
      );
      showChatNotice(
        "success",
        "Generated a shared room key for this conversation.",
      );
    } catch (error) {
      appendActivity(`Unable to generate a shared room key: ${error.message}`);
      showChatNotice(
        "error",
        `Unable to generate a shared room key: ${error.message}`,
      );
    }
  };

  const handleSend = async () => {
    if (!sessionReady) {
      appendActivity("Prepare the CNX session before sending a message.");
      return;
    }

    if (!selectedConversation || selectedConversation.source !== "cnx") {
      appendActivity(
        "Open a real CNX conversation first. Local provider-username drafts are not sent yet.",
      );
      return;
    }

    if ((selectedConversation.participantCount || 0) < 2) {
      appendActivity(
        "Add at least one other participant before sending the first message.",
      );
      return;
    }

    const trimmedMessageBody = messageBody.trim();

    if (!trimmedMessageBody) {
      appendActivity(
        "Write a message first. The browser will encrypt it before send.",
      );
      return;
    }

    await runWithLoadingAction("sendMessage", async () => {
      try {
        const clientCreatedAt = new Date().toISOString();
        const conversationKeyBase64 =
          await ensureConversationKeyForSelectedConversation();
        const envelope = await encryptConversationPlaintext({
          plaintext: trimmedMessageBody,
          conversationId: selectedConversation.id,
          conversationKeyBase64,
          clientCreatedAt,
        });
        const nextCiphertext = String(envelope.ciphertext || "").trim();
        const nextNonce = String(envelope.nonce || "").trim();
        const clientMessageId = `entzy-proof-${Date.now()}`;

        setCiphertext(nextCiphertext);
        setNonce(nextNonce);

        const backendResponse = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "sendMessage",
            ConversationId: selectedConversation.id,
            ClientMessageId: clientMessageId,
            CiphertextEnvelope: JSON.stringify(envelope),
          }),
        );

        const normalizedBackendResponse =
          normalizeSendMessageResponse(backendResponse);

        if (normalizedBackendResponse?.status === "ok") {
          const optimisticMessage = createMessageItem({
            id: clientMessageId,
            senderRole: "me",
            senderLabel: "Me",
            senderPointer: authenticatedPointer || "provider-backend",
            body: trimmedMessageBody,
            deliveryState: "sent",
            timestamp: formatTimestamp(clientCreatedAt),
          });
          setConversations((current) =>
            current.map((conversation) => {
              if (conversation.id !== selectedConversation.id) {
                return conversation;
              }

              const existingMessages = Array.isArray(conversation.messages)
                ? conversation.messages
                : [];
              const alreadyPresent = existingMessages.some(
                (message) => message.id === optimisticMessage.id,
              );

              return {
                ...conversation,
                messages: alreadyPresent
                  ? existingMessages
                  : [...existingMessages, optimisticMessage],
              };
            }),
          );

          updateStep("message", "ready");
          appendActivity(
            "Sent a real encrypted envelope through the provider backend. Plaintext stayed local and the browser handled the proof key automatically.",
          );
          setMessageBody("");
          try {
            await refreshConversationMessages(selectedConversation.id);
          } catch (refreshError) {
            appendActivity(
              `Message was sent, but the thread refresh did not complete yet: ${refreshError.message}`,
            );
          }
          return;
        }

        throw new Error(
          normalizedBackendResponse?.message ||
            "Provider backend did not return a valid sendMessage payload.",
        );
      } catch (error) {
        appendActivity(`Unable to send encrypted message: ${error.message}`);
      }
    });
  };

  const handleStartConversationTitleEdit = () => {
    if (!selectedConversation) {
      return;
    }

    setConversationTitleDraft(selectedConversation.title || "");
    setIsEditingConversationTitle(true);
  };

  const handleCancelConversationTitleEdit = () => {
    setConversationTitleDraft(selectedConversation?.title || "");
    setIsEditingConversationTitle(false);
  };

  const handleSaveConversationTitle = async () => {
    if (
      !sessionReady ||
      !selectedConversation ||
      selectedConversation.source !== "cnx"
    ) {
      appendActivity(
        "Open a real CNX conversation before updating the room title.",
      );
      return;
    }

    await runWithLoadingAction("updateConversation", async () => {
      try {
        const backendResponse = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "updateConversation",
            ConversationId: selectedConversation.id,
            ConversationTitle: conversationTitleDraft.trim() || undefined,
          }),
        );

        const normalizedBackendResponse =
          normalizeUpdateConversationResponse(backendResponse);

        if (
          normalizedBackendResponse?.status === "ok" &&
          normalizedBackendResponse.conversations.length
        ) {
          const mappedConversation = mapConversationSummary(
            normalizedBackendResponse.conversations[0],
          );

          setConversations((current) =>
            current.map((conversation) =>
              conversation.id === selectedConversation.id
                ? {
                    ...conversation,
                    ...mappedConversation,
                    title:
                      conversationTitleDraft.trim() ||
                      mappedConversation.title ||
                      conversation.title,
                    settings: {
                      ...(conversation.settings || {}),
                      ...(mappedConversation.settings || {}),
                      ...(conversationTitleDraft.trim()
                        ? { title: conversationTitleDraft.trim() }
                        : {}),
                    },
                    participants: conversation.participants || [],
                    participantsLoaded:
                      conversation.participantsLoaded || false,
                  }
                : conversation,
            ),
          );
          setIsEditingConversationTitle(false);
          appendActivity(
            `Updated the room title for ${mappedConversation.title}.`,
          );
          showChatNotice(
            "success",
            `Updated room title to ${mappedConversation.title}.`,
          );
          return;
        }

        throw new Error(
          "Provider backend did not return a valid updateConversation payload.",
        );
      } catch (error) {
        appendActivity(`Unable to update room title: ${error.message}`);
      }
    });
  };

  const handleSyncThread = async () => {
    if (
      !sessionReady ||
      !selectedConversation ||
      selectedConversation.source !== "cnx"
    ) {
      appendActivity("Open a real CNX conversation before syncing.");
      return;
    }

    await runWithLoadingAction("syncThread", async () => {
      try {
        await refreshConversationMessages(selectedConversation.id);
        updateStep("read", "ready");
        appendActivity(
          `Synced encrypted messages for ${selectedConversation.title}.`,
        );
      } catch (error) {
        appendActivity(`Unable to sync conversation: ${error.message}`);
      }
    });
  };

  useEffect(() => {
    if (!sessionForm.providerSessionId.trim() || sessionReady) {
      return undefined;
    }

    let cancelled = false;

    const restoreSession = async () => {
      setLoadingAction("restoreSession");
      try {
        const normalizedBaseUrl = normalizeSessionBaseUrl(sessionForm.baseUrl);
        let nextSession = {
          providerSessionId: sessionForm.providerSessionId.trim(),
          expiresAtEpochSeconds: sessionForm.expiresAtEpochSeconds,
          accountId: sessionForm.accountId.trim(),
        };

        const refreshResponse = await serviceGraphCall(
          "mutation",
          "cnxManager",
          buildCnxManagerVariables({
            Action: "refreshSession",
          }),
        );
        const refreshResult = normalizeActivationActionResponse(
          refreshResponse,
          {
            status: "verification_required",
            message:
              "CNX session refresh needs another verification step before chat can continue.",
          },
        );

        recordDebugState({
          action: "refreshSession",
          rawResponse: refreshResponse,
          normalizedResponse: refreshResult,
        });

        const restoredProviderSessionId =
          readProviderSessionIdFromPayload(refreshResult) ||
          nextSession.providerSessionId;

        if (
          refreshResult?.status === "session_ready" &&
          restoredProviderSessionId
        ) {
          nextSession = {
            providerSessionId: restoredProviderSessionId,
            expiresAtEpochSeconds:
              refreshResult.session?.expires_at_epoch_seconds || "",
            accountId:
              refreshResult.session?.cnx_user_id || nextSession.accountId || "",
          };

          persistSessionDraft({
            baseUrl: normalizedBaseUrl,
            providerSessionId: nextSession.providerSessionId,
            expiresAtEpochSeconds: nextSession.expiresAtEpochSeconds,
            accountId: nextSession.accountId,
          });
          setSessionForm((current) => ({
            ...current,
            providerSessionId: nextSession.providerSessionId,
            expiresAtEpochSeconds: nextSession.expiresAtEpochSeconds,
            accountId: nextSession.accountId,
          }));
          appendActivity(
            "Restored the persisted CNX session from the provider session store.",
          );
        } else {
          resetSessionState({
            clearPersistedDraft: shouldClearPersistedSessionOnRestoreFailure(
              refreshResult?.message,
            ),
            preserveBaseUrl: true,
            preserveActivity: true,
          });
          appendActivity(
            refreshResult?.message ||
              "Saved CNX session could not be restored automatically. Activate chat again.",
          );
          return;
        }

        if (cancelled) {
          return;
        }

        setSessionReady(true);
        setBootstrapOutcome("session_ready");
        updateStep("session", "ready");

        try {
          await loadConversations(nextSession.providerSessionId);
          if (cancelled) {
            return;
          }
          appendActivity(
            "Restored an existing CNX session from the provider session store.",
          );
        } catch (error) {
          if (cancelled) {
            return;
          }

          recordDebugState({
            action: "restoreSession",
            lastError: error.message,
          });
          appendActivity(
            `Restored the CNX session but could not load conversations yet: ${error.message}`,
          );
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        resetSessionState({
          clearPersistedDraft: false,
          preserveBaseUrl: true,
          preserveActivity: true,
        });
        recordDebugState({
          action: "restoreSession",
          lastError: error.message,
        });
        appendActivity(`Unable to restore saved CNX session: ${error.message}`);
      } finally {
        if (!cancelled) {
          setLoadingAction("");
        }
      }
    };

    restoreSession();

    return () => {
      cancelled = true;
    };
  }, [
    appendActivity,
    buildCnxManagerVariables,
    loadConversations,
    recordDebugState,
    resetSessionState,
    sessionForm.accountId,
    sessionForm.baseUrl,
    sessionForm.expiresAtEpochSeconds,
    sessionForm.providerSessionId,
    sessionReady,
    updateStep,
  ]);

  useEffect(() => {
    if (!sessionReady || !selectedConversationId || !selectedConversation) {
      return;
    }

    if (selectedConversation.source !== "cnx") {
      return;
    }

    if (selectedConversationKey) {
      return;
    }

    if (selectedConversationCryptoStatus !== "idle") {
      return;
    }

    let cancelled = false;

    const restoreConversationKey = async () => {
      setConversationCryptoStatusById((current) => ({
        ...current,
        [selectedConversationId]: "loading",
      }));

      try {
        const restoredConversationKey = await restoreConversationKeyFromBackend(
          selectedConversationId,
        );

        if (cancelled || !restoredConversationKey) {
          return;
        }

        appendActivity(
          `Restored the proof room key for ${selectedConversation.title} from provider-backed conversation crypto.`,
        );

        try {
          await refreshConversationMessages(
            selectedConversationId,
            restoredConversationKey,
          );
        } catch (error) {
          if (!cancelled) {
            appendActivity(
              `Restored the room key, but could not refresh ${selectedConversation.title} yet: ${error.message}`,
            );
          }
        }
      } catch (error) {
        if (!cancelled) {
          setConversationCryptoStatusById((current) => ({
            ...current,
            [selectedConversationId]: "error",
          }));
          appendActivity(
            `Unable to restore room encryption for ${selectedConversation.title}: ${error.message}`,
          );
        }
      }
    };

    restoreConversationKey();

    return () => {
      cancelled = true;
    };
  }, [
    appendActivity,
    refreshConversationMessages,
    restoreConversationKeyFromBackend,
    selectedConversation,
    selectedConversationCryptoStatus,
    selectedConversationId,
    selectedConversationKey,
    sessionReady,
  ]);

  useEffect(() => {
    if (!sessionReady || !selectedConversationId || !selectedConversation) {
      return;
    }

    if (selectedConversation.source !== "cnx") {
      return;
    }

    if (selectedConversation.participantsLoaded) {
      return;
    }

    let cancelled = false;

    const loadParticipants = async () => {
      try {
        await loadConversationParticipants(selectedConversationId);
      } catch (error) {
        if (!cancelled) {
          appendActivity(
            `Unable to load room members for ${selectedConversation.title}: ${error.message}`,
          );
        }
      }
    };

    loadParticipants();

    return () => {
      cancelled = true;
    };
  }, [
    appendActivity,
    loadConversationParticipants,
    selectedConversation,
    selectedConversationId,
    sessionReady,
  ]);

  useEffect(() => {
    if (!sessionReady || !selectedConversationId || !selectedConversation) {
      return;
    }

    if (selectedConversation.source !== "cnx") {
      return;
    }

    if (selectedConversation.messagesLoaded) {
      return;
    }

    if (loadingAction === "syncThread") {
      return;
    }

    let cancelled = false;

    const loadMessages = async () => {
      try {
        await runWithLoadingAction("syncThread", async () => {
          await refreshConversationMessages(selectedConversationId);
        });
      } catch (error) {
        if (!cancelled) {
          appendActivity(
            `Unable to load room messages for ${selectedConversation.title}: ${error.message}`,
          );
        }
      }
    };

    loadMessages();

    return () => {
      cancelled = true;
    };
  }, [
    appendActivity,
    loadingAction,
    refreshConversationMessages,
    runWithLoadingAction,
    selectedConversation,
    selectedConversationId,
    sessionReady,
  ]);

  const panelSx = {
    p: configEntzy.APP_SPACING_MD,
    borderRadius: configEntzy.BORDER_SIZE_XL,
    border: "1px solid rgba(255,255,255,0.08)",
    background:
      "linear-gradient(180deg, rgba(22, 24, 33, 0.94), rgba(12, 14, 20, 0.98))",
    boxShadow:
      "0 24px 80px rgba(0, 0, 0, 0.38), inset 0 1px 0 rgba(255,255,255,0.04)",
    backdropFilter: "blur(16px)",
  };

  const subtlePanelSx = {
    ...panelSx,
    background:
      "linear-gradient(180deg, rgba(18, 20, 28, 0.92), rgba(10, 12, 18, 0.98))",
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: configEntzy.BORDER_SIZE_XL,
      backgroundColor: "rgba(255,255,255,0.04)",
      color: "#eef2ff",
      "& fieldset": {
        borderColor: "rgba(255,255,255,0.08)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(255,122,41,0.42)",
      },
      "&.Mui-focused fieldset": {
        borderColor: "rgba(255,122,41,0.8)",
        boxShadow: "0 0 0 1px rgba(255,122,41,0.16)",
      },
    },
    "& .MuiInputLabel-root": {
      color: "rgba(197,204,225,0.72)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#ffb26b",
    },
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: "rgba(238,242,255,0.7)",
    },
  };

  if (!user || !user.connected) {
    return (
      <Box className="box-default">
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Sign In Required
        </Typography>
        <Typography
          variant="body1"
          sx={{ mt: configEntzy.APP_SPACING_MD, opacity: 0.82 }}
        >
          Connect to Entzy first before opening the experimental CNX activation
          route.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="cnx-exp-shell"
      sx={{
        p: configEntzy.APP_SPACING_MD,
      }}
    >
      <Box className="cnx-exp-ambient cnx-exp-ambient-left" />
      <Box className="cnx-exp-ambient cnx-exp-ambient-right" />
      <Stack spacing={3}>
        {chatNotice ? (
          <ActionAlert
            severity={chatNotice.severity}
            message={chatNotice.message}
            onClose={() => setChatNotice(null)}
          />
        ) : null}
        {sessionReady ? (
          <CnxHeader
            panelSx={panelSx}
            sessionReady={sessionReady}
            authenticatedPointer={authenticatedPointer}
            accountId={sessionForm.accountId}
          />
        ) : null}

        {sessionReady ? (
          <Box sx={panelSx}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{
                alignItems: { md: "center" },
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Chat
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.76 }}>
                  Provider-backed CNX session restored. Use the navigation to
                  move between chats, creation, settings, and diagnostics.
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flexWrap: "wrap",
                  justifyContent: { xs: "center", md: "flex-end" },
                }}
              >
                <Tooltip title="Chats">
                  <IconButton
                    aria-label="Chats"
                    sx={getNavIconSx("conversations")}
                    onClick={() => {
                      setActiveChatView("conversations");
                      setSelectedConversationId("");
                    }}
                  >
                    <FontAwesomeIcon
                      icon={iconChats}
                      fixedWidth
                      style={navIconInnerSx}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="New Chat">
                  <IconButton
                    aria-label="New Chat"
                    sx={getNavIconSx("newChat")}
                    onClick={() => {
                      setActiveChatView("newChat");
                      setSelectedConversationId("");
                    }}
                  >
                    <FontAwesomeIcon
                      icon={iconNewChat}
                      fixedWidth
                      style={navIconInnerSx}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Settings">
                  <IconButton
                    aria-label="Settings"
                    sx={getNavIconSx("settings")}
                    onClick={() => {
                      setActiveChatView("settings");
                      setSelectedConversationId("");
                    }}
                  >
                    <FontAwesomeIcon
                      icon={iconSettings}
                      fixedWidth
                      style={navIconInnerSx}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                  <span>
                    <IconButton
                      aria-label="Refresh"
                      sx={navIconSx}
                      disabled={isActionLoading("refreshSession")}
                      onClick={handleRefreshSession}
                    >
                      <FontAwesomeIcon
                        icon={iconRefresh}
                        fixedWidth
                        style={navIconInnerSx}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Debug">
                  <IconButton
                    aria-label="Debug"
                    sx={navIconSx}
                    onClick={() => setDebugDrawerOpen(true)}
                  >
                    <FontAwesomeIcon
                      icon={iconDebug}
                      fixedWidth
                      style={navIconInnerSx}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Disconnect">
                  <IconButton
                    aria-label="Disconnect"
                    sx={navIconSx}
                    onClick={handleDisconnect}
                  >
                    <FontAwesomeIcon
                      icon={iconDisconnect}
                      fixedWidth
                      style={navIconInnerSx}
                    />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        ) : (
          <Box sx={panelSx}>
            <Stack spacing={2} sx={{ mt: configEntzy.APP_SPACING_MD }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>
                CNX Chat by Entzy
              </Typography>
              <Typography
                variant="body1"
                className="cnx-exp-text-balanced"
                sx={{ opacity: 0.86 }}
              >
                Activate your portable chat session once, then come back to the
                same encrypted state through the provider-backed session store.
              </Typography>
              <Box
                sx={{
                  ...subtlePanelSx,
                  minHeight:
                    bootstrapOutcome === "Awaiting provider bootstrap"
                      ? 320
                      : "auto",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                {isRestoringSession ? (
                  <Box
                    sx={{
                      minHeight: 320,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      px: configEntzy.APP_SPACING_LG,
                    }}
                  >
                    <Box>
                      <CircularProgress
                        size={28}
                        thickness={5}
                        sx={{ color: "#ff9f5a" }}
                      />
                      <Typography
                        variant="body1"
                        sx={{ mt: 1.5, fontWeight: 600 }}
                      >
                        Checking saved chat session
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.75, opacity: 0.78 }}
                      >
                        Entzy is restoring your provider-backed CNX session
                        before showing chat.
                      </Typography>
                    </Box>
                  </Box>
                ) : bootstrapOutcome !== "Awaiting provider bootstrap" ? (
                  <>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {activationMeta.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
                      {activationMeta.detail}
                    </Typography>
                  </>
                ) : null}
                {!isRestoringSession &&
                bootstrapOutcome === "Awaiting provider bootstrap" ? (
                  <>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                      Activate chat
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 1.25, opacity: 0.72 }}
                    >
                      Start here to connect Entzy to your CNX chat identity.
                    </Typography>
                    <Stack spacing={2} sx={{ mt: 3, alignItems: "center" }}>
                      <MoreButton
                        variant="contained"
                        size="large"
                        type="button"
                        color="bright"
                        className="cnx-exp-primary-button cnx-exp-pill-button"
                        disabled={isActionLoading("prepareSession")}
                        text={
                          isActionLoading("prepareSession")
                            ? "Activating..."
                            : "Activate Chat"
                        }
                        onClick={handlePrepare}
                      />
                    </Stack>
                    <Box sx={{ mt: 4.5 }}>
                      <Link
                        href={cnxSiteUrl}
                        target="_blank"
                        rel="noreferrer"
                        underline="hover"
                        sx={{
                          color: "#ffcb9e",
                          fontSize: "0.95rem",
                          fontWeight: 500,
                          display: "inline-flex",
                        }}
                      >
                        Learn more at {cnxSiteUrl.replace(/^https?:\/\//, "")}
                      </Link>
                    </Box>
                  </>
                ) : null}
              </Box>
              {!isRestoringSession &&
              bootstrapOutcome === "integration_error" ? (
                <Box
                  sx={{
                    ...subtlePanelSx,
                    borderColor: "rgba(255,120,120,0.32)",
                    background:
                      "linear-gradient(180deg, rgba(96,18,18,0.34), rgba(16,18,25,0.98))",
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    Provider response mismatch
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                    The provider returned a CNX payload shape that this build
                    could not use safely. The user should not restart
                    registration or linking for this kind of error.
                  </Typography>
                  {debugState.lastError ? (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: "#ffb3b3" }}
                    >
                      {debugState.lastError}
                    </Typography>
                  ) : null}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{ mt: 2 }}
                  >
                    <MoreButton
                      variant="contained"
                      size="large"
                      type="button"
                      color="bright"
                      className="cnx-exp-pill-button"
                      disabled={isActionLoading("prepareSession")}
                      text={
                        isActionLoading("prepareSession")
                          ? "Retrying..."
                          : "Retry Chat Activation"
                      }
                      onClick={handlePrepare}
                    />
                    <MoreButton
                      variant="contained"
                      size="large"
                      type="button"
                      color="bright"
                      className="cnx-exp-pill-button"
                      text="Open Debug Drawer"
                      onClick={() => setDebugDrawerOpen(true)}
                    />
                  </Stack>
                </Box>
              ) : null}
              {!isRestoringSession && bootstrapOutcome === "error" ? (
                <Box
                  sx={{
                    ...subtlePanelSx,
                    borderColor: "rgba(255,180,120,0.26)",
                    background:
                      "linear-gradient(180deg, rgba(66,36,16,0.24), rgba(16,18,25,0.98))",
                  }}
                >
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    Chat activation needs another try
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                    Entzy did not receive a complete CNX activation state yet.
                    This is usually a retryable bootstrap issue rather than a
                    user-flow decision.
                  </Typography>
                  {debugState.lastError ? (
                    <Typography
                      variant="body2"
                      sx={{ mt: 1, color: "#ffd2ad" }}
                    >
                      {debugState.lastError}
                    </Typography>
                  ) : null}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{ mt: 2 }}
                  >
                    <MoreButton
                      variant="contained"
                      size="large"
                      type="button"
                      color="bright"
                      className="cnx-exp-pill-button"
                      disabled={isActionLoading("prepareSession")}
                      text={
                        isActionLoading("prepareSession")
                          ? "Retrying..."
                          : "Retry Activate Chat"
                      }
                      onClick={handlePrepare}
                    />
                    <MoreButton
                      variant="contained"
                      size="large"
                      type="button"
                      color="bright"
                      className="cnx-exp-pill-button"
                      text="Open Debug Drawer"
                      onClick={() => setDebugDrawerOpen(true)}
                    />
                  </Stack>
                </Box>
              ) : null}
              {!isRestoringSession &&
              bootstrapOutcome === "registration_required" ? (
                <Box sx={subtlePanelSx}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Step 1: Finish CNX setup
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
                    The real flow should now guide the user through CNX setup
                    with provider details already known. For this proof page, we
                    can simulate the handoff and continue into provider linking.
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: configEntzy.APP_SPACING_MD }}>
                    <TextField
                      fullWidth
                      label="Suggested CNX Pointer"
                      placeholder="::user.name"
                      value={activationSupport.cnxPointer}
                      sx={fieldSx}
                      onChange={(event) =>
                        setActivationSupport((current) => ({
                          ...current,
                          cnxPointer: event.target.value,
                        }))
                      }
                    />
                    <MoreButton
                      variant="contained"
                      size="large"
                      type="button"
                      color="bright"
                      className="cnx-exp-primary-button cnx-exp-pill-button"
                      disabled={isBusy && !isActionLoading("registerUser")}
                      text={
                        isActionLoading("registerUser")
                          ? "Working..."
                          : "Complete CNX Registration"
                      }
                      onClick={handleRegistrationStep}
                    />
                  </Stack>
                </Box>
              ) : null}
              {!isRestoringSession &&
              bootstrapOutcome === "verification_required" ? (
                <Box sx={subtlePanelSx}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {verificationIsLogin
                      ? "Step 1b: Verify CNX login"
                      : "Step 1b: Verify CNX setup"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
                    {verificationIsLogin
                      ? "CNX has detected an existing linked user and issued a login verification challenge. Enter the code to finish sign-in and continue into chat."
                      : "CNX has issued a registration challenge. Enter the verification code, then continue the activation flow."}
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: configEntzy.APP_SPACING_MD }}>
                    {!verificationIsLogin ? (
                      <TextField
                        fullWidth
                        label="Suggested CNX Pointer"
                        placeholder="::user.name"
                        value={activationSupport.cnxPointer}
                        sx={fieldSx}
                        onChange={(event) =>
                          setActivationSupport((current) => ({
                            ...current,
                            cnxPointer: event.target.value,
                          }))
                        }
                      />
                    ) : null}
                    <TextField
                      fullWidth
                      label="Challenge ID"
                      value={activationSupport.challengeId}
                      sx={fieldSx}
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      fullWidth
                      label="Verification Code"
                      placeholder="123456"
                      value={activationSupport.verificationCode}
                      sx={fieldSx}
                      onChange={(event) =>
                        setActivationSupport((current) => ({
                          ...current,
                          verificationCode: event.target.value,
                        }))
                      }
                    />
                    {!verificationIsLogin ? (
                      <Typography variant="body2" sx={{ opacity: 0.72 }}>
                        If the pointer is unavailable, update the pointer above
                        and retry this verification step with the same
                        challenge.
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ opacity: 0.72 }}>
                        This step should behave like a normal CNX login
                        confirmation for an existing user, not a new-pointer
                        registration step.
                      </Typography>
                    )}
                    <MoreButton
                      variant="contained"
                      size="large"
                      type="button"
                      color="bright"
                      className="cnx-exp-primary-button cnx-exp-pill-button"
                      disabled={
                        isBusy &&
                        !isActionLoading(
                          verificationIsLogin
                            ? "verifySession"
                            : "registerUser",
                        )
                      }
                      text={
                        isActionLoading(
                          verificationIsLogin
                            ? "verifySession"
                            : "registerUser",
                        )
                          ? "Working..."
                          : verificationIsLogin
                            ? "Verify Login And Continue"
                            : "Verify And Continue"
                      }
                      onClick={
                        verificationIsLogin
                          ? handleSessionVerificationStep
                          : handleRegistrationStep
                      }
                    />
                    <MoreButton
                      variant="contained"
                      size="large"
                      type="button"
                      color="bright"
                      className="cnx-exp-pill-button"
                      disabled={
                        isBusy && !isActionLoading("resendVerification")
                      }
                      text={
                        isActionLoading("resendVerification")
                          ? "Working..."
                          : "Send A New Code"
                      }
                      onClick={handleResendVerificationCode}
                    />
                  </Stack>
                </Box>
              ) : null}
              {bootstrapOutcome === "mfa_required" ? (
                <Box sx={subtlePanelSx}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Step 2: Complete CNX MFA
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
                    CNX has confirmed the linked user and now needs the
                    configured second factor before chat can activate.
                  </Typography>
                  <Stack spacing={1.5} sx={{ mt: configEntzy.APP_SPACING_MD }}>
                    {hasMultipleMfaMethods ? (
                      <Box
                        className="cnx-exp-mfa-methods"
                        role="tablist"
                        aria-label="Available MFA methods"
                      >
                        {mfaSupportsTotp ? (
                          <button
                            type="button"
                            role="tab"
                            aria-selected={activeMfaMethod === "totp"}
                            className={`cnx-exp-mfa-tab ${activeMfaMethod === "totp" ? "active" : ""}`}
                            onClick={() => setActiveMfaMethod("totp")}
                          >
                            Authenticator App
                          </button>
                        ) : null}
                        {mfaSupportsPasskey ? (
                          <button
                            type="button"
                            role="tab"
                            aria-selected={activeMfaMethod === "passkey"}
                            className={`cnx-exp-mfa-tab ${activeMfaMethod === "passkey" ? "active" : ""}`}
                            onClick={() => setActiveMfaMethod("passkey")}
                          >
                            Passkey
                          </button>
                        ) : null}
                      </Box>
                    ) : null}
                    {mfaSupportsTotp && activeMfaMethod === "totp" ? (
                      <>
                        {hasMultipleMfaMethods ? (
                          <MoreButton
                            variant="contained"
                            size="large"
                            type="button"
                            color="bright"
                            className="cnx-exp-nav-button cnx-exp-pill-button"
                            text="Back To MFA Options"
                            onClick={() => setActiveMfaMethod("")}
                          />
                        ) : null}
                        <Typography variant="body2" sx={{ opacity: 0.72 }}>
                          Enter the current 6-digit code from your authenticator
                          app.
                        </Typography>
                        <TextField
                          fullWidth
                          label="Authenticator Code"
                          placeholder="123456"
                          value={activationSupport.verificationCode}
                          sx={fieldSx}
                          onChange={(event) =>
                            setActivationSupport((current) => ({
                              ...current,
                              verificationCode: event.target.value,
                            }))
                          }
                        />
                        <MoreButton
                          variant="contained"
                          size="large"
                          type="button"
                          color="bright"
                          className="cnx-exp-primary-button cnx-exp-pill-button"
                          disabled={isBusy && !isActionLoading("verifyMfaTotp")}
                          text={
                            isActionLoading("verifyMfaTotp")
                              ? "Working..."
                              : "Verify Authenticator App"
                          }
                          onClick={handleVerifyMfaTotp}
                        />
                      </>
                    ) : null}
                    {mfaSupportsPasskey && activeMfaMethod === "passkey" ? (
                      <>
                        {hasMultipleMfaMethods ? (
                          <MoreButton
                            variant="contained"
                            size="large"
                            type="button"
                            color="bright"
                            className="cnx-exp-nav-button cnx-exp-pill-button"
                            text="Back To MFA Options"
                            onClick={() => setActiveMfaMethod("")}
                          />
                        ) : null}
                        <Typography variant="body2" sx={{ opacity: 0.72 }}>
                          Use your CNX passkey to complete MFA in a secure
                          popup, then Entzy will finish verification
                          automatically.
                        </Typography>
                        <MoreButton
                          variant="contained"
                          size="large"
                          type="button"
                          color="bright"
                          className="cnx-exp-primary-button cnx-exp-pill-button"
                          disabled={
                            isBusy && !isActionLoading("startMfaPasskey")
                          }
                          text={
                            isActionLoading("startMfaPasskey")
                              ? "Working..."
                              : "Verify With Passkey"
                          }
                          onClick={handleStartMfaPasskey}
                        />
                      </>
                    ) : null}
                  </Stack>
                </Box>
              ) : null}
              {bootstrapOutcome === "link_required" ? (
                <Box sx={subtlePanelSx}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Step 2: Your New Connection Point Is Ready
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
                    Your new CNX connection point has been created successfully.
                    Continue once to connect it to Entzy Chat and move straight
                    into the final verification step if CNX asks for it.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
                    Provider user: {bootstrapDraft.providerUserId || "Missing"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
                    Link consent:{" "}
                    {activationSupport.agreedToLink ? "Confirmed" : "Pending"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
                    This is a one-off connection step so the new pointer can
                    start chatting as this Entzy user.
                  </Typography>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.5}
                    sx={{ mt: configEntzy.APP_SPACING_MD }}
                  >
                    <MoreButton
                      variant="contained"
                      size="large"
                      type="button"
                      color="bright"
                      className="cnx-exp-primary-button cnx-exp-pill-button"
                      disabled={
                        isBusy && !isActionLoading("confirmProviderLink")
                      }
                      text={
                        isActionLoading("confirmProviderLink")
                          ? "Working..."
                          : "Continue To Connect To Chat"
                      }
                      onClick={handleProviderLinkStep}
                    />
                    <MoreButton
                      variant="contained"
                      size="large"
                      type="button"
                      color="bright"
                      className="cnx-exp-pill-button"
                      disabled={isBusy && !isActionLoading("prepareSession")}
                      text="Retry Activate Chat"
                      onClick={handlePrepare}
                    />
                  </Stack>
                </Box>
              ) : null}
              {bootstrapOutcome === "session_ready" ? (
                <Box sx={subtlePanelSx}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Step 3: Continue into the DM
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.82 }}>
                    Activation is complete. The next user-visible transition
                    should go straight into the DM conversation bootstrap for
                    the selected partner.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.72 }}>
                    CNX user session connected for{" "}
                    {sessionForm.accountId || "current user"}.
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          </Box>
        )}

        {sessionReady ? (
          <CnxChatRoom
            panelSx={panelSx}
            subtlePanelSx={subtlePanelSx}
            fieldSx={fieldSx}
            activeView={activeChatView}
            recipientPointer={recipientPointer}
            setRecipientPointer={setRecipientPointer}
            memberPointer={memberPointer}
            setMemberPointer={setMemberPointer}
            messageBody={messageBody}
            setMessageBody={setMessageBody}
            selectedConversationKey={selectedConversationKey}
            isActionLoading={isActionLoading}
            handleCreateConversation={handleCreateConversation}
            handleAddParticipant={handleAddParticipant}
            handleRemoveParticipant={handleRemoveParticipant}
            handleSend={handleSend}
            handleSyncThread={handleSyncThread}
            conversationTitleDraft={conversationTitleDraft}
            setConversationTitleDraft={setConversationTitleDraft}
            isEditingConversationTitle={isEditingConversationTitle}
            handleStartConversationTitleEdit={handleStartConversationTitleEdit}
            handleCancelConversationTitleEdit={
              handleCancelConversationTitleEdit
            }
            handleSaveConversationTitle={handleSaveConversationTitle}
            conversations={conversations}
            selectedConversation={selectedConversation}
            setSelectedConversationId={setSelectedConversationId}
            isConversationListLoading={isActionLoading("listConversations")}
          />
        ) : null}
      </Stack>

      <CnxDebug
        open={debugDrawerOpen}
        onClose={() => setDebugDrawerOpen(false)}
        bootstrapDraft={bootstrapDraft}
        setBootstrapDraft={setBootstrapDraft}
        bootstrapOutcome={bootstrapOutcome}
        debugState={debugState}
        sessionReady={sessionReady}
        sessionForm={sessionForm}
        setSessionForm={setSessionForm}
        persistedSessionDebug={persistedSessionDebug}
        selectedConversation={selectedConversation}
        selectedConversationKey={selectedConversationKey}
        setSelectedConversationKey={setSelectedConversationKey}
        ciphertext={ciphertext}
        nonce={nonce}
        handleGenerateConversationKey={handleGenerateConversationKey}
        activity={activity}
        steps={steps}
        fieldSx={fieldSx}
        subtlePanelSx={subtlePanelSx}
        loadingAction={loadingAction}
      />
      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: "1px solid rgba(255,167,96,0.18)",
            background:
              "linear-gradient(180deg, rgba(15,18,26,0.98), rgba(9,11,17,0.99))",
            color: "#eef2ff",
            boxShadow:
              "0 0 0 1px rgba(255,255,255,0.03) inset, 0 18px 44px rgba(0,0,0,0.34)",
            minWidth: { sm: 420 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, textAlign: "left" }}>
          Disconnect CNX chat?
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important", textAlign: "left" }}>
          <Typography variant="body2" sx={{ opacity: 0.82, textAlign: "left" }}>
            This removes the saved provider-backed CNX session from this browser
            and returns you to the activation screen.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <MoreButton
            variant="contained"
            size="medium"
            type="button"
            color="bright"
            className="cnx-exp-nav-button"
            text="Cancel"
            onClick={() => setDisconnectDialogOpen(false)}
          />
          <MoreButton
            variant="contained"
            size="medium"
            type="button"
            color="bright"
            className="cnx-exp-primary-button cnx-exp-pill-button"
            text="Disconnect"
            onClick={handleConfirmDisconnect}
          />
        </DialogActions>
      </Dialog>
      <Dialog
        open={passkeyOverlayOpen}
        onClose={() => setPasskeyOverlayOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 4,
            border: "1px solid rgba(255,255,255,0.08)",
            background:
              "linear-gradient(180deg, rgba(18,20,28,0.96), rgba(10,12,18,0.98))",
            color: "#eef2ff",
            boxShadow:
              "0 24px 80px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.05)",
            minWidth: { xs: "calc(100% - 32px)", sm: 420 },
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 700, textAlign: "left" }}>
          Secure passkey check
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important", textAlign: "left" }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
            <CircularProgress
              size={22}
              thickness={5}
              sx={{ color: "#ff9f5a" }}
            />
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.92 }}>
                {passkeyOverlayMessage}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.68 }}>
                A secure CNX window should appear near the center of your
                screen. In native WebKit shells or in-app browsers, popup
                placement is best-effort and may be handled by the host app.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
          <MoreButton
            variant="contained"
            size="medium"
            type="button"
            color="bright"
            className="cnx-exp-nav-button cnx-exp-pill-button"
            text="Hide Overlay"
            onClick={() => setPasskeyOverlayOpen(false)}
          />
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function CnxIndex(props) {
  if (configEntzy.IS_PRODUCTION) {
    return null;
  }

  return <CnxIndexExperimental {...props} />;
}

export default CnxIndex;
